import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpFromLine,
  BarChart3,
  Building2,
  Calendar,
  CalendarRange,
  Check,
  ChevronDown,
  ChevronRight,
  Columns3,
  Diamond,
  Download,
  Flag,
  HelpCircle,
  History,
  ListTree,
  Loader2,
  Maximize2,
  MessageCircle,
  Minimize2,
  PencilLine,
  Search,
  SlidersHorizontal,
  StickyNote,
  Table2,
  Undo2,
  User,
  Users,
  X,
  Network,
} from 'lucide-react';
import { addDays, format, isValid, parseISO, startOfDay } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import epcProjectService from '../../../services/epcProjectService';
import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../types/scheduleTask';
import {
  assignableMembersForOrg,
  departmentHeadForOrg,
  membershipDisplayName,
  orgUnitKey,
  orgUnitName,
  taskAssignedUserId,
  taskOrgUnitId,
} from './utils/epcScheduleDepartmentAssignment';
import { countChangedRows } from './utils/tableEditChanges';
import { downloadScheduleExport } from './utils/downloadScheduleExport';
import { isGovernanceRole } from '../project/team/teamCapabilities';
import EpcScheduleDepartmentAssignView from './EpcScheduleDepartmentAssignView';
import EpcScheduleMemberAssignView from './EpcScheduleMemberAssignView';
import EpcScheduleEditor from './EpcScheduleEditor';
import EpcScheduleTimeline, { buildCanonicalPayload, downloadTasksCsv } from './EpcScheduleTimeline';
import EpcScheduleTaskDetailPanel from './detail/EpcScheduleTaskDetailPanel';
import { twMerge } from 'tailwind-merge';
import {
  parsePredecessorLinks,
  parseSuccessorLinks,
  predecessorDisplayLabel,
  successorDisplayLabel,
} from './utils/epcScheduleDependencies';
import {
  getPredecessorsMsColumnText,
  getResourceAssignmentsLines,
  getSuccessorsMsColumnText,
  splitMsDependencySegments,
} from './utils/epcScheduleColumnFormat';
import {
  getWbsLevelBadgeClasses,
  getWbsTableRowClasses,
  normalizeWbsLevel,
} from './utils/epcScheduleWbsLevelStyle';
import { getDepartmentChipClasses, departmentDotClasses } from './utils/epcScheduleDepartmentColors';
import { WbsTaskTreeGutter } from './WbsTaskTreeGutter';
import { ScheduleDatesCell } from './ScheduleDatesHoverCard';
import { EpcSchedulePredecessorsColumn, EpcScheduleRawMsDependencyColumn } from './EpcScheduleDependencyColumn';
import { ScheduleDependencyJumpOverlay } from './ScheduleDependencyJumpOverlay';
import { calendarSpanInclusive, formatDateCell, formatDateIso, parseTaskDay } from './utils/epcScheduleDatesDisplay';
import EpcScheduleTaskMessagesModal from './EpcScheduleTaskMessagesModal';
import { epcScheduleTaskStableKey } from './utils/epcScheduleTaskStableKey';
import { EpcScheduleDateRangePicker } from './EpcScheduleDateRangePicker';
import {
  formatDurationCompact,
  isTaskMilestone,
  parsePercentComplete,
} from './utils/epcScheduleMsProjectFormat';

const BRAND = {
  accent: '#339274',
  accentMuted: 'rgba(51, 146, 116, 0.12)',
};

function numLevel(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function isSummaryTask(t: EpcScheduleTaskRow): boolean {
  const s = t.isSummary;
  return s === true || s === 'true' || s === 1 || s === '1';
}

function taskName(t: EpcScheduleTaskRow): string {
  return String(t.name ?? '').trim();
}

function parseDateBound(raw: string): Date | null {
  if (!raw.trim()) return null;
  const d = parseISO(raw.length >= 10 ? raw.slice(0, 10) : raw);
  return isValid(d) ? d : null;
}

function fmtDateShort(iso: string): string {
  const d = parseDateBound(iso);
  return d ? format(d, 'MMM d') : iso;
}

/** When both ends are set, order by calendar day so From > To still filters correctly. */
function overlapFilterBounds(
  fromD: Date | null,
  toD: Date | null
): { from: Date | null; to: Date | null } {
  if (!fromD && !toD) return { from: null, to: null };
  if (fromD && toD) {
    const a = startOfDay(fromD);
    const b = startOfDay(toD);
    return a.getTime() <= b.getTime() ? { from: a, to: b } : { from: b, to: a };
  }
  return {
    from: fromD ? startOfDay(fromD) : null,
    to: toD ? startOfDay(toD) : null,
  };
}

/** True if task start/finish window overlaps [fromD, toD] (inclusive by day). */
function taskOverlapsWindow(
  t: EpcScheduleTaskRow,
  fromD: Date | null,
  toD: Date | null
): boolean {
  if (!fromD && !toD) return true;
  const sRaw = t.start;
  const fRaw = t.finish;
  let S = sRaw != null && String(sRaw) ? parseISO(String(sRaw).slice(0, 10)) : null;
  let F = fRaw != null && String(fRaw) ? parseISO(String(fRaw).slice(0, 10)) : null;
  if (S && !isValid(S)) S = null;
  if (F && !isValid(F)) F = null;
  if (!S && !F) return true;

  const taskStart = startOfDay(S ?? F!);
  const taskEnd = startOfDay(F ?? S!);
  const rangeStart = fromD ?? taskStart;
  const rangeEnd = toD ?? taskEnd;
  return taskStart <= rangeEnd && taskEnd >= rangeStart;
}

/** End index (exclusive) of subtree rooted at i in WBS order. */
function subtreeEnd(tasks: EpcScheduleTaskRow[], i: number): number {
  const L = numLevel(tasks[i].outlineLevel);
  let j = i + 1;
  while (j < tasks.length && numLevel(tasks[j].outlineLevel) > L) {
    j += 1;
  }
  return j;
}

function visibleByCollapse(
  tasks: EpcScheduleTaskRow[],
  collapsed: Set<number>,
  index: number
): boolean {
  for (let j = 0; j < index; j += 1) {
    if (!isSummaryTask(tasks[j]) || !collapsed.has(j)) continue;
    const end = subtreeEnd(tasks, j);
    if (index > j && index < end) return false;
  }
  return true;
}

/** Latest ready MPP import — shown as the "current revision" chip in the planning-grid header. */
export type EpcScheduleRevisionDetail = {
  version: number;
  originalFileName?: string;
  source?: string;
  createdAt?: string;
};

export type EpcSchedulePanelProps = {
  projectId: string;
  tasks: EpcScheduleTaskRow[];
  /** After save or external refresh — reload snapshot from parent. */
  onScheduleChanged: () => void | Promise<void>;
  /** Full snapshot for save/export merge (optional). */
  fullSnapshot?: Record<string, unknown> | null;
  projectCode?: string;
  canUpdate?: boolean;
  /** Highest ready import/snapshot version — drives the "Saving creates revision R<n+1>" note. */
  currentVersion?: number;
  currentRevision?: EpcScheduleRevisionDetail | null;
};

type StudioMode = 'list' | 'timeline' | 'manage' | 'departments' | 'members';

type TableDensity = 'compact' | 'comfortable';

type MsColumnKey =
  | 'id'
  | 'indicators'
  | 'duration'
  | 'start'
  | 'finish'
  | 'percent'
  | 'predecessors'
  | 'department'
  | 'owner'
  | 'discussion';

type MsColumnConfig = {
  key: MsColumnKey;
  label: string;
  shortLabel: string;
  description: string;
  /** Group heading (Microsoft Project column groups). */
  group: 'task' | 'schedule' | 'progress' | 'links' | 'assignment' | 'info';
  /** Fixed width (used for `<colgroup>` col widths and sticky calculations). */
  width: string;
};

const MS_COLUMNS: readonly MsColumnConfig[] = [
  { key: 'id', label: '#', shortLabel: '#', description: 'Row number (MS Project Task ID).', group: 'task', width: '38px' },
  { key: 'indicators', label: 'Indicators', shortLabel: 'i', description: 'Milestone, summary, notes, and discussion markers.', group: 'task', width: '44px' },
  { key: 'predecessors', label: 'Dependence', shortLabel: 'Deps', description: 'Driving tasks with link type and lag.', group: 'links', width: '150px' },
  { key: 'start', label: 'Start', shortLabel: 'Start', description: 'Task start date.', group: 'schedule', width: '116px' },
  { key: 'finish', label: 'Finish', shortLabel: 'Finish', description: 'Task finish date.', group: 'schedule', width: '116px' },
  { key: 'duration', label: 'Duration', shortLabel: 'Dur', description: 'Calendar duration (compact MS Project units).', group: 'schedule', width: '82px' },
  { key: 'percent', label: '% Complete', shortLabel: '% C', description: 'Progress with an inline MS Project-style bar.', group: 'progress', width: '128px' },
  { key: 'department', label: 'Department', shortLabel: 'Dept', description: 'EPC org unit bucket.', group: 'assignment', width: '150px' },
  { key: 'owner', label: 'DH', shortLabel: 'DH', description: 'Department head and task assignee.', group: 'assignment', width: '172px' },
  { key: 'discussion', label: 'Discussion', shortLabel: 'Chat', description: 'Task-level discussion thread.', group: 'info', width: '72px' },
];

const DEFAULT_COL_WIDTHS: Record<MsColumnKey, number> = {
  id: 38, indicators: 44, duration: 82, start: 116, finish: 116,
  percent: 128, predecessors: 150, department: 150,
  owner: 172, discussion: 72,
};

const DEFAULT_NAME_COL_WIDTH = 340;
const MIN_COL_WIDTH = 40;
const MIN_NAME_COL_WIDTH = 140;
const STORAGE_KEY_COL_WIDTHS = 'epc.schedule.colWidths.v1';
const STORAGE_KEY_NAME_COL_WIDTH = 'epc.schedule.nameColWidth.v1';

function loadStoredColWidths(): Record<MsColumnKey, number> {
  if (typeof window === 'undefined') return { ...DEFAULT_COL_WIDTHS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_COL_WIDTHS);
    if (!raw) return { ...DEFAULT_COL_WIDTHS };
    const parsed = JSON.parse(raw) as Partial<Record<MsColumnKey, unknown>>;
    const merged: Record<MsColumnKey, number> = { ...DEFAULT_COL_WIDTHS };
    (Object.keys(merged) as MsColumnKey[]).forEach((k) => {
      const v = parsed?.[k];
      if (typeof v === 'number' && Number.isFinite(v) && v >= MIN_COL_WIDTH) merged[k] = v;
    });
    return merged;
  } catch {
    return { ...DEFAULT_COL_WIDTHS };
  }
}

function loadStoredNameColWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_NAME_COL_WIDTH;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_NAME_COL_WIDTH);
    if (!raw) return DEFAULT_NAME_COL_WIDTH;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= MIN_NAME_COL_WIDTH) return n;
  } catch {}
  return DEFAULT_NAME_COL_WIDTH;
}

function getTaskNameTextClasses(L: number, isSummary: boolean): string {
  if (isSummary) {
    if (L <= 1) return 'text-[14px] font-extrabold text-[#0B3B2E] tracking-tight';
    if (L <= 2) return 'text-[13.5px] font-bold text-[#0B3B2E] tracking-tight';
    if (L <= 3) return 'text-[12.5px] font-bold text-[#0B3B2E]';
    if (L <= 4) return 'text-[12px] font-semibold text-[#1F5A47]';
    return 'text-[11.5px] font-semibold text-[#1F5A47]';
  }
  if (L <= 1) return 'text-[13.5px] font-medium text-[#2A1F17]';
  if (L <= 2) return 'text-[13px] font-medium text-[#2A1F17]';
  if (L <= 3) return 'text-[12px] font-normal text-[#2A1F17]';
  if (L <= 5) return 'text-[11.5px] font-normal text-[#7C8A82]';
  return 'text-[11px] font-normal text-[#7C8A82]';
}

const DEFAULT_MS_COLUMN_VISIBILITY: Record<MsColumnKey, boolean> = {
  id: true,
  indicators: true,
  duration: true,
  start: true,
  finish: true,
  percent: false,
  predecessors: true,
  department: true,
  owner: true,
  discussion: false,
};

const COLUMN_GROUP_LABEL: Record<MsColumnConfig['group'], string> = {
  task: 'Task',
  schedule: 'Schedule',
  progress: 'Progress',
  links: 'Dependencies',
  assignment: 'Assignment',
  info: 'Info',
};

const COLUMN_GROUP_ACCENT: Record<MsColumnConfig['group'], string> = {
  task: 'bg-[#F2EADB] text-[#0B3B2E]',
  schedule: 'bg-[rgba(31,90,71,0.10)] text-[#1F5A47]',
  progress: 'bg-[rgba(51,146,116,0.10)] text-[#1F5A47]',
  links: 'bg-[rgba(201,169,97,0.14)] text-[#8A5A08]',
  assignment: 'bg-[rgba(51,146,116,0.10)] text-[#1F5A47]',
  info: 'bg-[rgba(201,169,97,0.14)] text-[#8A5A08]',
};

const MAX_ROWS = 800;

export default function EpcSchedulePanel({
  projectId,
  tasks,
  onScheduleChanged,
  fullSnapshot = null,
  projectCode = 'schedule',
  canUpdate = false,
  currentVersion,
  currentRevision = null,
}: EpcSchedulePanelProps) {
  const { user: authUser } = useAuth();
  const u = authUser as { _id?: string; id?: string } | null | undefined;
  const currentUserId = u?._id ? String(u._id) : u?.id ? String(u.id) : undefined;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [levelMin, setLevelMin] = useState<number | ''>('');
  const [levelMax, setLevelMax] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  /** Row indices to return to after predecessor/successor jumps (LIFO). */
  const [linkNavStack, setLinkNavStack] = useState<number[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deptFilterId, setDeptFilterId] = useState('');
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [studioMode, setStudioMode] = useState<StudioMode>('list');
  const [studioFullView, setStudioFullView] = useState<boolean>(false);
  /** Personal scope filter — 'all' (full project) or 'mine' (only my org/team/tasks). */
  type Scope = 'all' | 'mine';
  const [scope, setScope] = useState<Scope>('all');
  const [scopeInitialized, setScopeInitialized] = useState(false);
  const [editedTasks, setEditedTasks] = useState<EpcScheduleTaskRow[]>([]);
  const [deptAssignTasks, setDeptAssignTasks] = useState<EpcScheduleTaskRow[]>([]);
  const [memberAssignTasks, setMemberAssignTasks] = useState<EpcScheduleTaskRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [exportingMpp, setExportingMpp] = useState(false);
  const [orgUnits, setOrgUnits] = useState<EpcProjectOrgUnit[]>([]);
  const [memberships, setMemberships] = useState<EpcProjectMembershipRow[]>([]);
  const [taskMessagesModal, setTaskMessagesModal] = useState<{
    taskKey: string;
    taskTitle: string;
    rowLabel: string;
  } | null>(null);
  const [messageSummaryByKey, setMessageSummaryByKey] = useState<
    Record<string, { total: number; unreadFromOthers: number }>
  >({});
  const [density] = useState<TableDensity>('compact');
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const helpMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<MsColumnKey, boolean>>(
    () => ({ ...DEFAULT_MS_COLUMN_VISIBILITY })
  );
  const columnsMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const [gridFullView, setGridFullView] = useState(false);
  const [colWidths, setColWidths] = useState<Record<MsColumnKey, number>>(() => loadStoredColWidths());
  const [nameColWidth, setNameColWidth] = useState<number>(() => loadStoredNameColWidth());
  const resizeDragRef = useRef<{ key: MsColumnKey; startX: number; startWidth: number } | null>(null);

  /* ===== Table inline-edit mode (spec §4.1) ===== */
  const [tableEdit, setTableEdit] = useState(false);
  const [draftTasks, setDraftTasks] = useState<EpcScheduleTaskRow[]>([]);
  const prevColVisibilityRef = useRef<Record<MsColumnKey, boolean> | null>(null);

  const enterTableEdit = useCallback(() => {
    prevColVisibilityRef.current = columnVisibility;
    /* dept + assignee are editable fields — make their columns visible while editing */
    setColumnVisibility((v) => ({ ...v, department: true, owner: true }));
    setDraftTasks(tasks.map((t) => ({ ...t })));
    setSelectedIndex(null);
    setTableEdit(true);
  }, [columnVisibility, tasks]);

  const exitTableEdit = useCallback(() => {
    if (prevColVisibilityRef.current) setColumnVisibility(prevColVisibilityRef.current);
    prevColVisibilityRef.current = null;
    setTableEdit(false);
    setDraftTasks([]);
  }, []);

  const updateDraft = useCallback((idx: number, patch: Partial<EpcScheduleTaskRow>) => {
    setDraftTasks((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }, []);

  /** Same clearing rule the Edit sub-tab uses: an assignee that no longer fits the new org is dropped. */
  const patchDraftDepartment = useCallback(
    (idx: number, nextOrg: string) => {
      const oid = nextOrg.trim() || undefined;
      setDraftTasks((prev) =>
        prev.map((t, i) => {
          if (i !== idx) return t;
          const patch: Partial<EpcScheduleTaskRow> = { epcOrgUnitId: oid };
          const cur = taskAssignedUserId(t);
          if (cur && oid) {
            const allowed = new Set(assignableMembersForOrg(memberships, oid).map((m) => m.userId));
            if (!allowed.has(cur)) patch.assignedUserId = undefined;
          } else if (!oid) {
            patch.assignedUserId = undefined;
          }
          return { ...t, ...patch };
        })
      );
    },
    [memberships]
  );

  const tableEditChangeCount = useMemo(
    () => (tableEdit ? countChangedRows(tasks, draftTasks) : 0),
    [tableEdit, tasks, draftTasks]
  );

  const saveTableEdit = useCallback(async () => {
    try {
      setSaving(true);
      await epcProjectService.saveSchedule(projectId, buildCanonicalPayload(fullSnapshot, draftTasks));
      toast.success('New schedule revision saved');
      await onScheduleChanged();
      exitTableEdit();
    } catch (e) {
      console.error(e);
      /* §7 — stay in edit mode with the draft intact */
      toast.error('Could not save schedule — your edits are kept');
    } finally {
      setSaving(false);
    }
  }, [projectId, fullSnapshot, draftTasks, onScheduleChanged, exitTableEdit]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_COL_WIDTHS, JSON.stringify(colWidths));
    } catch {}
  }, [colWidths]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_NAME_COL_WIDTH, String(nameColWidth));
    } catch {}
  }, [nameColWidth]);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const anchor = columnsMenuAnchorRef.current;
      if (anchor && anchor.contains(e.target as Node)) return;
      const menu = document.getElementById('epc-schedule-columns-menu');
      if (menu && menu.contains(e.target as Node)) return;
      setColumnsMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColumnsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [columnsMenuOpen]);

  useEffect(() => {
    if (!helpMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const anchor = helpMenuAnchorRef.current;
      if (anchor && anchor.contains(e.target as Node)) return;
      const menu = document.getElementById('epc-schedule-help-menu');
      if (menu && menu.contains(e.target as Node)) return;
      setHelpMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHelpMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [helpMenuOpen]);

  const startColResize = useCallback((key: MsColumnKey, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeDragRef.current = { key, startX: e.clientX, startWidth: colWidths[key] };
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (mv: MouseEvent) => {
      const drag = resizeDragRef.current;
      if (!drag) return;
      const delta = mv.clientX - drag.startX;
      const newW = Math.max(MIN_COL_WIDTH, drag.startWidth + delta);
      setColWidths((prev) => (prev[drag.key] === newW ? prev : { ...prev, [drag.key]: newW }));
    };
    const onUp = () => {
      resizeDragRef.current = null;
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths]);

  const resetColWidths = useCallback(() => {
    setColWidths({ ...DEFAULT_COL_WIDTHS });
    setNameColWidth(DEFAULT_NAME_COL_WIDTH);
  }, []);

  const startNameColResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = nameColWidth;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (mv: MouseEvent) => {
      const delta = mv.clientX - startX;
      setNameColWidth(Math.max(MIN_NAME_COL_WIDTH, startW + delta));
    };
    const onUp = () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [nameColWidth]);

  const totalGridWidth = useMemo(() => {
    let w = nameColWidth;
    if (columnVisibility.id) w += colWidths.id;
    if (columnVisibility.indicators) w += colWidths.indicators;
    if (columnVisibility.duration) w += colWidths.duration;
    if (columnVisibility.start) w += colWidths.start;
    if (columnVisibility.finish) w += colWidths.finish;
    if (columnVisibility.percent) w += colWidths.percent;
    if (columnVisibility.predecessors) w += colWidths.predecessors;
    if (columnVisibility.department) w += colWidths.department;
    if (columnVisibility.owner) w += colWidths.owner;
    if (columnVisibility.discussion) w += colWidths.discussion;
    return w;
  }, [colWidths, nameColWidth, columnVisibility]);

  const visibleMsColumns = useMemo(
    () => MS_COLUMNS.filter((c) => columnVisibility[c.key]),
    [columnVisibility]
  );
  const visibleGroups = useMemo(() => {
    const groups: { group: MsColumnConfig['group']; colSpan: number }[] = [];
    for (const col of visibleMsColumns) {
      const last = groups[groups.length - 1];
      if (last && last.group === col.group) {
        last.colSpan += 1;
      } else {
        groups.push({ group: col.group, colSpan: 1 });
      }
    }
    return groups;
  }, [visibleMsColumns]);
  const toggleColumn = useCallback((key: MsColumnKey) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const resetColumns = useCallback(() => {
    setColumnVisibility({ ...DEFAULT_MS_COLUMN_VISIBILITY });
  }, []);

  const visibleOptionalCount = useMemo(
    () => (Object.values(columnVisibility) as boolean[]).filter(Boolean).length,
    [columnVisibility]
  );

  const taskKeysFingerprint = useMemo(() => tasks.map(epcScheduleTaskStableKey).join('\x01'), [tasks]);

  useEffect(() => {
    if (!projectId || tasks.length === 0) return;
    const keys = taskKeysFingerprint.split('\x01').filter(Boolean);
    if (keys.length === 0) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void epcProjectService
        .getScheduleTaskMessagesSummary(projectId, keys)
        .then((data) => {
          if (!cancelled) setMessageSummaryByKey(data);
        })
        .catch(() => {
          if (!cancelled) setMessageSummaryByKey({});
        });
    }, 380);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [projectId, taskKeysFingerprint]);

  const syncTaskMessageSummary = useCallback(
    (taskKey: string, summary: { total: number; unreadFromOthers: number }) => {
      setMessageSummaryByKey((prev) => ({ ...prev, [taskKey]: summary }));
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ou, mem] = await Promise.all([
          epcProjectService.listOrgUnits(projectId),
          epcProjectService.listMemberships(projectId),
        ]);
        if (!cancelled) {
          setOrgUnits(ou);
          setMemberships(mem);
        }
      } catch {
        if (!cancelled) {
          setOrgUnits([]);
          setMemberships([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    const s = searchParams.get('studio');
    if (
      s === 'timeline' ||
      s === 'list' ||
      s === 'manage' ||
      s === 'departments' ||
      s === 'members'
    ) {
      setStudioMode(s);
    }
    /* "full=1" → fullscreen editor; only meaningful when studio=manage */
    setStudioFullView(searchParams.get('full') === '1' && s === 'manage');
  }, [searchParams]);

  const setStudioModeAndUrl = useCallback(
    (mode: StudioMode) => {
      setStudioMode(mode);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === 'list') {
            next.delete('studio');
          } else {
            next.set('studio', mode);
          }
          /* leaving manage drops the full-view flag */
          if (mode !== 'manage') next.delete('full');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** The current user's membership in this project (if any). */
  const mine = useMemo(
    () =>
      currentUserId
        ? memberships.find((m) => m.userId === currentUserId && !m.inactive)
        : undefined,
    [memberships, currentUserId],
  );

  /** True when the user is a non-governance member (dept_head / manager / engineer / etc.). */
  const isPersonalRole = !!mine && !isGovernanceRole(mine.role);

  /** Engineers reporting to me — only meaningful when I'm a manager. */
  const myEngineerIds = useMemo(() => {
    if (!mine || mine.role !== 'manager' || !currentUserId) return new Set<string>();
    const ids = new Set<string>();
    for (const m of memberships) {
      if (m.role === 'site_engineer' && m.reportsToUserId === currentUserId) {
        ids.add(m.userId);
      }
    }
    return ids;
  }, [mine, memberships, currentUserId]);

  /** The friendly label for the user's personal scope ("department", "team", "tasks"). */
  const scopeNoun = useMemo<'department' | 'team' | 'tasks'>(() => {
    if (!mine) return 'tasks';
    if (mine.role === 'department_head') return 'department';
    if (mine.role === 'manager') return 'team';
    return 'tasks';
  }, [mine]);

  /** Sync `scope` with URL — and default to 'mine' for personal roles on first load. */
  useEffect(() => {
    const s = searchParams.get('scope');
    if (s === 'mine' || s === 'all') {
      setScope(s);
      setScopeInitialized(true);
      return;
    }
    /* No URL scope set — once memberships have loaded, default personal roles to 'mine'. */
    if (!scopeInitialized && memberships.length > 0) {
      setScope(isPersonalRole ? 'mine' : 'all');
      setScopeInitialized(true);
    }
  }, [searchParams, isPersonalRole, memberships, scopeInitialized]);

  /**
   * Deep link: `?focusTask=<taskKey>` (extension-request notifications and the PMO
   * pending-extensions panel) opens that task's discussion thread once rows load.
   * Fires at most once, then removes the param so refresh/back stay clean.
   */
  const focusTaskHandledRef = useRef(false);
  useEffect(() => {
    if (focusTaskHandledRef.current) return;
    const fk = searchParams.get('focusTask');
    if (!fk || tasks.length === 0) return;
    focusTaskHandledRef.current = true;
    const idx = tasks.findIndex((t) => epcScheduleTaskStableKey(t) === fk);
    if (idx >= 0) {
      const t = tasks[idx]!;
      setTaskMessagesModal({
        taskKey: fk,
        taskTitle: taskName(t) || 'Task',
        rowLabel: `Row ${idx + 1} · WBS level ${normalizeWbsLevel(t.outlineLevel) || '—'}`,
      });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('focusTask');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, tasks, setSearchParams]);

  const setScopeAndUrl = useCallback(
    (s: Scope) => {
      setScope(s);
      setScopeInitialized(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          /* For personal roles default is 'mine', so URL stays clean when on default;
             explicit override always recorded. */
          if ((isPersonalRole && s === 'mine') || (!isPersonalRole && s === 'all')) {
            next.delete('scope');
          } else {
            next.set('scope', s);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, isPersonalRole],
  );

  /** Decide if a given task is in the personal scope. */
  const inPersonalScope = useCallback(
    (t: EpcScheduleTaskRow): boolean => {
      if (!mine) return true;
      if (isGovernanceRole(mine.role)) return true;
      if (mine.role === 'department_head' && mine.orgUnitId) {
        return taskOrgUnitId(t) === mine.orgUnitId;
      }
      if (mine.role === 'manager') {
        const a = taskAssignedUserId(t);
        if (!a) return false;
        return a === currentUserId || myEngineerIds.has(a);
      }
      if (mine.role === 'site_engineer') {
        return taskAssignedUserId(t) === currentUserId;
      }
      return true;
    },
    [mine, currentUserId, myEngineerIds],
  );

  const toggleStudioFullView = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('full') === '1') next.delete('full');
        else {
          next.set('studio', 'manage');
          next.set('full', '1');
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (studioMode === 'manage') {
      setEditedTasks(tasks.map((t) => ({ ...t })));
    }
  }, [studioMode, tasks]);

  useLayoutEffect(() => {
    if (studioMode === 'departments') {
      setDeptAssignTasks(tasks.map((t) => ({ ...t })));
    }
  }, [studioMode, tasks]);

  useLayoutEffect(() => {
    if (studioMode === 'members') {
      setMemberAssignTasks(tasks.map((t) => ({ ...t })));
    }
  }, [studioMode, tasks]);

  const dirtyManage = useMemo(() => {
    if (studioMode !== 'manage') return false;
    return JSON.stringify(editedTasks) !== JSON.stringify(tasks);
  }, [studioMode, editedTasks, tasks]);

  const dirtyDeptAssign = useMemo(() => {
    if (studioMode !== 'departments') return false;
    return JSON.stringify(deptAssignTasks) !== JSON.stringify(tasks);
  }, [studioMode, deptAssignTasks, tasks]);

  const dirtyMemberAssign = useMemo(() => {
    if (studioMode !== 'members') return false;
    return JSON.stringify(memberAssignTasks) !== JSON.stringify(tasks);
  }, [studioMode, memberAssignTasks, tasks]);

  const persistSchedule = useCallback(async () => {
    // Revision guard — completed tasks are immutable (server enforces this too);
    // in-progress tasks may be re-planned but only after an explicit warning.
    const originals = tasks as unknown as Record<string, unknown>[];
    const drafts = editedTasks as unknown as Record<string, unknown>[];
    const completedChanged: string[] = [];
    const inProgressChanged: string[] = [];
    const len = Math.max(originals.length, drafts.length);
    for (let i = 0; i < len; i += 1) {
      const a = originals[i];
      const b = drafts[i];
      if (!a || !b) continue;
      if (JSON.stringify(a) === JSON.stringify(b)) continue;
      const pct = typeof a['percentComplete'] === 'number' ? (a['percentComplete'] as number) : 0;
      const completed = pct >= 100 || Boolean(a['actualFinish']);
      const started = pct > 0 || Boolean(a['actualStart']);
      const label = `${a['wbs'] ?? ''} ${a['name'] ?? ''}`.trim() || `row ${i + 1}`;
      if (completed) completedChanged.push(label);
      else if (started) inProgressChanged.push(`${label} (${pct}%)`);
    }
    if (completedChanged.length > 0) {
      toast.error(
        `Completed tasks cannot be edited: ${completedChanged.slice(0, 3).join(', ')}${completedChanged.length > 3 ? ` +${completedChanged.length - 3} more` : ''}. Undo those changes to save.`,
        { duration: 7000 },
      );
      return;
    }
    const doSave = async () => {
      try {
        setSaving(true);
        await epcProjectService.saveSchedule(projectId, buildCanonicalPayload(fullSnapshot, editedTasks));
        toast.success('New schedule version saved');
        await onScheduleChanged();
        setStudioModeAndUrl('list');
      } catch (e) {
        console.error(e);
        toast.error('Could not save schedule');
      } finally {
        setSaving(false);
      }
    };
    if (inProgressChanged.length > 0) {
      Modal.confirm({
        title: 'Editing in-progress tasks',
        content: `${inProgressChanged.slice(0, 4).join(', ')}${inProgressChanged.length > 4 ? ` +${inProgressChanged.length - 4} more` : ''} ${inProgressChanged.length === 1 ? 'is' : 'are'} already in progress on site. Re-planning changes the crew's dates — recorded progress is kept. Continue?`,
        okText: 'Save revision',
        cancelText: 'Cancel',
        onOk: doSave,
      });
      return;
    }
    await doSave();
  }, [projectId, fullSnapshot, tasks, editedTasks, onScheduleChanged, setStudioModeAndUrl]);

  const persistDeptSchedule = useCallback(async () => {
    try {
      setSaving(true);
      await epcProjectService.saveSchedule(projectId, buildCanonicalPayload(fullSnapshot, deptAssignTasks));
      toast.success('New schedule version saved');
      await onScheduleChanged();
      setStudioModeAndUrl('list');
    } catch (e) {
      console.error(e);
      toast.error('Could not save schedule');
    } finally {
      setSaving(false);
    }
  }, [projectId, fullSnapshot, deptAssignTasks, onScheduleChanged, setStudioModeAndUrl]);

  const persistMemberSchedule = useCallback(async () => {
    try {
      setSaving(true);
      await epcProjectService.saveSchedule(projectId, buildCanonicalPayload(fullSnapshot, memberAssignTasks));
      toast.success('Member assignments saved');
      await onScheduleChanged();
      setStudioModeAndUrl('list');
    } catch (e) {
      console.error(e);
      toast.error('Could not save assignments');
    } finally {
      setSaving(false);
    }
  }, [projectId, fullSnapshot, memberAssignTasks, onScheduleChanged, setStudioModeAndUrl]);

  const exportMpp = useCallback(async () => {
    try {
      setExportingMpp(true);
      const { format } = await downloadScheduleExport(
        projectId,
        `${projectCode.replace(/[^a-zA-Z0-9._-]/g, '_')}-schedule`
      );
      if (format === 'mpp') {
        toast.success('.mpp downloaded — opens directly in Microsoft Project');
      } else {
        toast.success('MS Project XML downloaded — open in Project, then Save As .mpp', {
          duration: 6000,
        });
      }
    } catch (e) {
      console.error(e);
      toast.error('Schedule export failed — check the MPP converter service');
    } finally {
      setExportingMpp(false);
    }
  }, [projectId, projectCode]);

  const maxOutline = useMemo(() => {
    let m = 0;
    for (const t of tasks) {
      m = Math.max(m, numLevel(t.outlineLevel));
    }
    return m;
  }, [tasks]);

  const levelOptions = useMemo(() => {
    const opts: number[] = [];
    for (let L = 1; L <= Math.max(1, maxOutline); L += 1) {
      opts.push(L);
    }
    return opts;
  }, [maxOutline]);

  const fromD = parseDateBound(dateFrom);
  const toD = parseDateBound(dateTo);
  const overlapBounds = useMemo(() => overlapFilterBounds(fromD, toD), [fromD, toD]);

  const filteredIndices = useMemo(() => {
    const q = search.trim().toLowerCase();
    const minL = levelMin === '' ? 1 : levelMin;
    const maxL = levelMax === '' ? 99 : levelMax;
    const lo = Math.min(minL, maxL);
    const hi = Math.max(minL, maxL);

    const out: number[] = [];
    for (let i = 0; i < tasks.length; i += 1) {
      const t = tasks[i];
      if (!visibleByCollapse(tasks, collapsed, i)) continue;

      const L = numLevel(t.outlineLevel);
      if (L < lo || L > hi) continue;

      /* Personal scope (dept_head → my dept · manager → me + my engineers · engineer → me). */
      if (scope === 'mine' && !inPersonalScope(t)) continue;

      if (deptFilterId) {
        const oid = taskOrgUnitId(t);
        if (oid !== deptFilterId) continue;
      }

      if (q) {
        const name = taskName(t).toLowerCase();
        const deptN = orgUnitName(orgUnits, taskOrgUnitId(t)).toLowerCase();
        const assignN = membershipDisplayName(memberships, taskAssignedUserId(t)).toLowerCase();
        const headN = (() => {
          const h = departmentHeadForOrg(memberships, taskOrgUnitId(t));
          return (h?.userName || '').toLowerCase();
        })();
        if (!name.includes(q) && !deptN.includes(q) && !assignN.includes(q) && !headN.includes(q)) {
          const preds = parsePredecessorLinks(t.predecessors);
          const succs = parseSuccessorLinks(t.successors);
          const hitPred = preds.some((p) =>
            predecessorDisplayLabel(p, tasks).toLowerCase().includes(q)
          );
          const hitSucc = succs.some((s) =>
            successorDisplayLabel(s, tasks).toLowerCase().includes(q)
          );
          const predMs = getPredecessorsMsColumnText(t, tasks).toLowerCase();
          const succMs = getSuccessorsMsColumnText(t, tasks).toLowerCase();
          const resBlob = getResourceAssignmentsLines(t).join(' ').toLowerCase();
          const dateBlob = `${formatDateCell(t.start)} ${formatDateIso(t.start)} ${formatDateCell(t.finish)} ${formatDateIso(t.finish)}`.toLowerCase();
          if (
            !hitPred &&
            !hitSucc &&
            !predMs.includes(q) &&
            !succMs.includes(q) &&
            !resBlob.includes(q) &&
            !dateBlob.includes(q)
          ) {
            continue;
          }
        }
      }

      if (overlapBounds.from || overlapBounds.to) {
        if (!taskOverlapsWindow(t, overlapBounds.from, overlapBounds.to)) continue;
      }

      out.push(i);
    }
    return out;
  }, [
    tasks,
    collapsed,
    search,
    levelMin,
    levelMax,
    overlapBounds,
    deptFilterId,
    orgUnits,
    memberships,
    scope,
    inPersonalScope,
  ]);

  /** Must be stable across renders: `slice()` every render breaks useLayoutEffects that depend on it (setDepLine loop). */
  const displayIndices = useMemo(() => filteredIndices.slice(0, MAX_ROWS), [filteredIndices]);
  const truncated = filteredIndices.length > MAX_ROWS;

  /** Glance-able KPIs computed across the *filtered* rows (leaf tasks only for status counts). */
  const planningKpis = useMemo(() => {
    const todayMs = startOfDay(new Date()).getTime();
    let leafTotal = 0;
    let done = 0;
    let inProgress = 0;
    let notStarted = 0;
    let overdue = 0;
    let milestones = 0;
    let progressSum = 0;
    let progressDenom = 0;
    for (const i of filteredIndices) {
      const t = tasks[i];
      if (!t) continue;
      const isMs = isTaskMilestone(t);
      const isSum = isSummaryTask(t);
      if (isMs) milestones += 1;
      if (isSum) continue; // status counts are leaf-only
      leafTotal += 1;
      const pct = parsePercentComplete(t.percentComplete);
      const finishMs = (() => {
        const d = parseTaskDay(t.finish);
        return d ? startOfDay(d).getTime() : null;
      })();
      if (pct != null) {
        progressSum += pct;
        progressDenom += 1;
      }
      if (pct != null && pct >= 100) {
        done += 1;
      } else if (pct != null && pct > 0) {
        inProgress += 1;
        if (finishMs != null && finishMs < todayMs) overdue += 1;
      } else {
        notStarted += 1;
        if (finishMs != null && finishMs < todayMs) overdue += 1;
      }
    }
    const avg = progressDenom > 0 ? Math.round(progressSum / progressDenom) : 0;
    const donePct = leafTotal > 0 ? Math.round((done / leafTotal) * 100) : 0;
    return { leafTotal, done, inProgress, notStarted, overdue, milestones, avg, donePct };
  }, [filteredIndices, tasks]);

  const rowElementsRef = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const scrollTargetRef = useRef<number | null>(null);
  /** When set, successful scroll pushes `from` onto `linkNavStack` (forward link only). */
  const pendingLinkJumpRef = useRef<{ from: number; to: number } | null>(null);
  /** Last row scrolled to via predecessor/successor link (modal stays closed; used for back animation). */
  const lastLinkNavTargetRef = useRef<number | null>(null);
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const depJumpRef = useRef<{ from: number; to: number } | null>(null);
  const [depJump, setDepJump] = useState<{ from: number; to: number } | null>(null);
  const [depLine, setDepLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(
    null
  );
  depJumpRef.current = depJump;

  const measureDepLine = useCallback(() => {
    const j = depJumpRef.current;
    if (!j) {
      setDepLine(null);
      return;
    }
    const a = rowElementsRef.current.get(j.from)?.getBoundingClientRect();
    const b = rowElementsRef.current.get(j.to)?.getBoundingClientRect();
    if (!a || !b) {
      setDepLine(null);
      return;
    }
    setDepLine({
      x1: a.right - 6,
      y1: a.top + a.height / 2,
      x2: b.left + 28,
      y2: b.top + b.height / 2,
    });
  }, []);

  const clearDepJump = useCallback(() => {
    depJumpRef.current = null;
    setDepJump(null);
    setDepLine(null);
  }, []);

  /** Jump to linked task: scroll/highlight only — does not open the task sheet (use Back to return). */
  const jumpToLinkedTask = useCallback(
    (toRow: number, fromRow: number) => {
      if (fromRow !== toRow) {
        pendingLinkJumpRef.current = { from: fromRow, to: toRow };
      } else {
        pendingLinkJumpRef.current = null;
      }
      scrollTargetRef.current = toRow;
      setSelectedIndex(null);
      setDepJump({ from: fromRow, to: toRow });
      setCollapsed((prev) => {
        const next = new Set(prev);
        for (let j = 0; j < toRow; j += 1) {
          if (!isSummaryTask(tasks[j])) continue;
          const end = subtreeEnd(tasks, j);
          if (toRow > j && toRow < end) next.delete(j);
        }
        return next;
      });
      setStudioModeAndUrl('list');
    },
    [tasks, setStudioModeAndUrl]
  );

  const jumpBackToPreviousTask = useCallback(() => {
    setLinkNavStack((stack) => {
      if (stack.length === 0) return stack;
      const prev = stack[stack.length - 1]!;
      const fromCurrent = lastLinkNavTargetRef.current;
      pendingLinkJumpRef.current = null;
      scrollTargetRef.current = prev;
      setSelectedIndex(null);
      if (fromCurrent != null && fromCurrent !== prev) {
        setDepJump({ from: fromCurrent, to: prev });
      }
      lastLinkNavTargetRef.current = prev;
      setStudioModeAndUrl('list');
      return stack.slice(0, -1);
    });
  }, [setStudioModeAndUrl]);

  useLayoutEffect(() => {
    const i = scrollTargetRef.current;
    if (i == null) return;
    scrollTargetRef.current = null;
    const pending = pendingLinkJumpRef.current;
    if (!filteredIndices.includes(i)) {
      toast.error('That task is hidden by the current filters. Reset filters to see it in the table.');
      pendingLinkJumpRef.current = null;
      lastLinkNavTargetRef.current = null;
      clearDepJump();
      return;
    }
    if (!displayIndices.includes(i)) {
      toast.error(
        'That task matches filters but is beyond the first 800 rows shown. Tighten filters to locate it.'
      );
      pendingLinkJumpRef.current = null;
      lastLinkNavTargetRef.current = null;
      clearDepJump();
      return;
    }
    if (pending && pending.from !== pending.to) {
      setLinkNavStack((s) => [...s, pending.from]);
    }
    pendingLinkJumpRef.current = null;
    lastLinkNavTargetRef.current = i;
    rowElementsRef.current.get(i)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timers = [40, 120, 350, 700, 1100].map((ms) => window.setTimeout(() => measureDepLine(), ms));
    return () => timers.forEach(clearTimeout);
  }, [filteredIndices, collapsed, displayIndices, measureDepLine, clearDepJump]);

  useLayoutEffect(() => {
    measureDepLine();
  }, [depJump, filteredIndices, collapsed, displayIndices, measureDepLine]);

  useEffect(() => {
    if (!depJump) return;
    const el = scheduleScrollRef.current;
    const onMove = () => measureDepLine();
    el?.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    return () => {
      el?.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
    };
  }, [depJump, measureDepLine]);

  useEffect(() => {
    if (!depJump) return;
    const t = window.setTimeout(() => clearDepJump(), 3200);
    return () => clearTimeout(t);
  }, [depJump, clearDepJump]);

  const toggleCollapse = useCallback(
    (i: number) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i);
        else next.add(i);
        return next;
      });
    },
    []
  );

  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const collapseLevel1 = useCallback(() => {
    const next = new Set<number>();
    tasks.forEach((t, i) => {
      if (isSummaryTask(t) && numLevel(t.outlineLevel) === 1) {
        next.add(i);
      }
    });
    setCollapsed(next);
  }, [tasks]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setLevelMin('');
    setLevelMax('');
    setDateFrom('');
    setDateTo('');
    setDeptFilterId('');
  }, []);

  const dateFilterFieldId = useId();
  const scopeFilterFieldId = useId();
  const timelineScopeFilterFieldId = useId();
  const timelineDateFilterFieldId = useId();
  const wbsMinSelectId = `${scopeFilterFieldId}-wbs-min`;
  const wbsMaxSelectId = `${scopeFilterFieldId}-wbs-max`;
  const departmentSelectId = `${scopeFilterFieldId}-department`;
  const dateRangeTriggerDomId = `${dateFilterFieldId}-range-trigger`;
  const startToEndBtnRef = useRef<HTMLButtonElement>(null);
  const timelineStartToEndBtnRef = useRef<HTMLButtonElement>(null);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (search.trim()) n += 1;
    if (levelMin !== '' || levelMax !== '') n += 1;
    if (dateFrom || dateTo) n += 1;
    if (deptFilterId) n += 1;
    return n;
  }, [search, levelMin, levelMax, dateFrom, dateTo, deptFilterId]);

  /** Parsed schedule filter dates for the overlap window UI. */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedIndex != null) setSelectedIndex(null);
        else if (gridFullView) setGridFullView(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, gridFullView]);

  if (tasks.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative overflow-hidden rounded-2xl border border-dashed border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.96)] p-8 sm:p-10"
      >
        {/* Engraved banknote inner rule */}
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-[11px] border border-dashed border-[rgba(11,59,46,0.18)]"
          style={{ inset: 7 }}
        />
        <div className="relative flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B3B2E] text-[#F8F2E2] shadow-sm ring-1 ring-[rgba(201,169,97,0.5)]">
            <ListTree className="h-7 w-7" strokeWidth={1.75} />
          </div>
          <h3 className="mt-5 text-lg font-extrabold tracking-tight text-[#0B3B2E]">No schedule yet</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#7C8A82]">
            Use <span className="font-semibold text-[#2A1F17]">MPP import</span> (in the schedule toolbar once tasks exist) or
            the button below to upload a{' '}
            <code className="rounded-md bg-[#fcfaf3] px-1.5 py-0.5 font-mono text-xs text-[#2A1F17] shadow-sm ring-1 ring-[rgba(11,59,46,0.14)]">.mpp</code>{' '}
            file or import from URL. Tasks appear here after a successful import.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              type="button"
              onClick={() => navigate('../mpp', { relative: 'path' })}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[rgba(11,59,46,0.45)] bg-[#264f3c] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_18px_-12px_rgba(38,79,60,.5)] transition hover:bg-[#2d5c47]"
            >
              <Network className="h-4 w-4" aria-hidden />
              MPP import
            </button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  const linkBackTarget = linkNavStack.length > 0 ? linkNavStack[linkNavStack.length - 1]! : null;
  const linkBackTitle =
    linkBackTarget != null ? taskName(tasks[linkBackTarget]) || `Row ${linkBackTarget + 1}` : '';

  return (
    <div className="space-y-4">
      <ScheduleDependencyJumpOverlay line={depLine} />
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-center gap-2 rounded-2xl border border-[rgba(11,59,46,0.30)] bg-[#F8F2E2]/85 p-2 shadow-[0_10px_28px_-20px_rgba(42,31,23,0.40)]"
      >
        <span className="hidden pl-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#7C8A82] sm:inline">
          View
        </span>
        <button
          type="button"
          onClick={() => setStudioModeAndUrl('list')}
          className={twMerge(
            'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition',
            studioMode === 'list'
              ? 'border-[rgba(11,59,46,0.30)] bg-[rgba(250,247,238,0.96)] text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(201,169,97,0.35)]'
              : 'border-transparent bg-transparent text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)] hover:text-[#0B3B2E]'
          )}
        >
          <Table2 className="h-4 w-4" />
          Table
        </button>
        <button
          type="button"
          onClick={() => setStudioModeAndUrl('departments')}
          className={twMerge(
            'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition',
            studioMode === 'departments'
              ? 'border-[rgba(11,59,46,0.30)] bg-[rgba(250,247,238,0.96)] text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(201,169,97,0.35)]'
              : 'border-transparent bg-transparent text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)] hover:text-[#0B3B2E]'
          )}
        >
          <Building2 className="h-4 w-4" />
          Departments
        </button>
        <button
          type="button"
          onClick={() => setStudioModeAndUrl('timeline')}
          className={twMerge(
            'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition',
            studioMode === 'timeline'
              ? 'border-[rgba(11,59,46,0.30)] bg-[rgba(250,247,238,0.96)] text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(201,169,97,0.35)]'
              : 'border-transparent bg-transparent text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)] hover:text-[#0B3B2E]'
          )}
        >
          <BarChart3 className="h-4 w-4" />
          Timeline
        </button>
        <button
          type="button"
          title={!canUpdate ? 'View-only: save requires epcProject.update' : undefined}
          onClick={() => setStudioModeAndUrl('manage')}
          className={twMerge(
            'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition',
            studioMode === 'manage'
              ? 'border-[rgba(11,59,46,0.30)] bg-[rgba(250,247,238,0.96)] text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(201,169,97,0.35)]'
              : 'border-transparent bg-transparent text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)] hover:text-[#0B3B2E]'
          )}
        >
          <PencilLine className="h-4 w-4" />
          Create Revision
        </button>
        {mine && (mine.role === 'department_head' || mine.role === 'manager') ? (
          <button
            type="button"
            onClick={() => setStudioModeAndUrl('members')}
            className={twMerge(
              'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition',
              studioMode === 'members'
                ? 'border-[rgba(11,59,46,0.30)] bg-[rgba(250,247,238,0.96)] text-[#0B3B2E] shadow-sm ring-1 ring-[rgba(201,169,97,0.35)]'
                : 'border-transparent bg-transparent text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)] hover:text-[#0B3B2E]',
            )}
            title={
              mine.role === 'department_head'
                ? 'Assign your department tasks to a manager'
                : 'Assign your tasks to an engineer'
            }
          >
            <Users className="h-4 w-4" />
            {mine.role === 'department_head' ? 'My Team' : 'My Engineers'}
          </button>
        ) : null}
        {linkNavStack.length > 0 && linkBackTarget != null ? (
          <button
            type="button"
            onClick={jumpBackToPreviousTask}
            title={`Back to row ${linkBackTarget + 1}: ${linkBackTitle}`}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-2 text-[11px] font-bold uppercase tracking-wide text-[#0B3B2E] shadow-sm transition hover:bg-[rgba(11,59,46,0.04)]"
          >
            <Undo2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Back
          </button>
        ) : null}

        {/* Personal scope toggle — visible only for non-governance project members */}
        {isPersonalRole ? (
          <div
            className="ml-1 inline-flex items-center gap-0 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] p-0.5 shadow-sm"
            title={`Switch between your ${scopeNoun} and the full project schedule`}
          >
            <button
              type="button"
              onClick={() => setScopeAndUrl('mine')}
              className={twMerge(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide transition',
                scope === 'mine'
                  ? 'bg-[#264f3c] text-white shadow'
                  : 'text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)]',
              )}
            >
              <User className="h-3.5 w-3.5" />
              My {scopeNoun}
            </button>
            <button
              type="button"
              onClick={() => setScopeAndUrl('all')}
              className={twMerge(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide transition',
                scope === 'all'
                  ? 'bg-[#264f3c] text-white shadow'
                  : 'text-[#2A1F17]/70 hover:bg-[rgba(11,59,46,0.05)]',
              )}
            >
              <Users className="h-3.5 w-3.5" />
              All
            </button>
          </div>
        ) : null}
        <div className="flex w-full flex-wrap items-center gap-2 border-t border-[rgba(11,59,46,0.14)] pt-2 sm:ml-auto sm:w-auto sm:border-t-0 sm:pt-0">
          <button
            type="button"
            onClick={() => navigate('../mpp', { relative: 'path' })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.45)] bg-[#264f3c] px-3 py-2 text-xs font-semibold text-white shadow-[0_8px_18px_-12px_rgba(38,79,60,.5)] hover:bg-[#2d5c47]"
            title="Microsoft Project (.mpp) upload, URL import, and history"
          >
            <Network className="h-3.5 w-3.5" aria-hidden />
            MPP import
          </button>
          <button
            type="button"
            disabled={exportingMpp}
            onClick={() => void exportMpp()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-2 text-xs font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)] disabled:opacity-50"
          >
            {exportingMpp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export for MS Project
          </button>
        </div>
      </motion.div>

      {studioMode === 'list' ? (
        <>
          <div className="rounded-2xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.96)] shadow-[0_14px_36px_-22px_rgba(42,31,23,0.40)]">
            <div className="flex flex-wrap items-center gap-2 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/80 px-3 py-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                className={twMerge(
                  'inline-flex min-h-[2.25rem] items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition',
                  filtersOpen
                    ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white shadow-sm'
                    : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                )}
              >
                <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#C9A961]" aria-hidden />
                <span>Filters</span>
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-[#C9A961] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#2A1F17]">
                    {activeFilterCount}
                  </span>
                ) : null}
                <ChevronDown
                  className={twMerge('h-4 w-4 shrink-0 transition-transform', filtersOpen ? 'rotate-180 text-white/70' : 'text-[#7C8A82]')}
                  aria-hidden
                />
              </button>
              <span
                className="inline-flex items-center rounded-md border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2 py-1 font-mono text-[11px] tabular-nums text-[#7C8A82]"
                title="Rows matching filters · total tasks in schedule"
              >
                <span className="font-semibold text-[#0B3B2E]">{filteredIndices.length}</span>
                <span className="mx-0.5 text-[rgba(11,59,46,0.25)]">/</span>
                {tasks.length}
              </span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1F5A47] hover:bg-[rgba(51,146,116,0.10)]"
                  >
                    <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Clear all
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={expandAll}
                  className="rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-1.5 text-xs font-semibold text-[#0B3B2E] shadow-sm hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={collapseLevel1}
                  className="rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-1.5 text-xs font-semibold text-[#0B3B2E] shadow-sm hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]"
                >
                  Collapse L1
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {filtersOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-visible"
                >
                  <div className="border-t border-[rgba(11,59,46,0.10)] bg-[#F2EADB]/50 px-2 py-2 sm:px-2.5 sm:py-2">
                    <div
                      className="flex flex-col gap-2.5 sm:gap-3"
                      role="group"
                      aria-label="Schedule table filters"
                    >
                      {/* Box 1 — find in schedule, WBS, department */}
                      <div className="overflow-hidden rounded-lg border border-[rgba(11,59,46,0.22)] bg-[#FCFBF6] shadow-sm">
                        <div className="flex flex-col gap-4 p-3 sm:p-4">
                          <label className="group/find min-w-0">
                            <span className="relative z-20 mb-2 flex items-start gap-3">
                              <span
                                className="relative z-20 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0B3B2E] text-[#F8F2E2] shadow-sm ring-1 ring-[rgba(201,169,97,0.5)]"
                                aria-hidden
                              >
                                <Search className="h-4 w-4" strokeWidth={2.5} />
                              </span>
                              <span className="min-w-0 pt-0.5">
                                <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#2A1F17]">
                                  Find in schedule
                                </span>
                                <span className="mt-0.5 block text-[11px] font-medium leading-snug text-[#7C8A82]">
                                  Match task name, WBS codes, predecessors, successors, or resource lines — results update
                                  as you type.
                                </span>
                              </span>
                            </span>
                            <div className="relative z-10 overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] shadow-sm transition-[box-shadow,border-color] focus-within:z-20 focus-within:border-[#0B3B2E] focus-within:ring-2 focus-within:ring-[rgba(166,206,57,0.22)]">
                              <div
                                className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-[#C9A961]"
                                aria-hidden
                              />
                              <Search
                                className="pointer-events-none absolute left-3.5 top-1/2 h-[1.125rem] w-[1.125rem] -translate-y-1/2 text-[#339274] transition group-focus-within/find:scale-105 group-focus-within/find:text-[#1F5A47]"
                                aria-hidden
                              />
                              <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search the live schedule…"
                                autoComplete="off"
                                className="relative w-full min-h-11 rounded-2xl border-0 bg-transparent py-2.5 pl-11 pr-3 text-sm font-medium text-[#2A1F17] placeholder:font-normal placeholder:text-[#7C8A82] focus:outline-none"
                              />
                            </div>
                          </label>

                          <div className="flex flex-col gap-4 border-t border-[rgba(11,59,46,0.10)] pt-4 lg:flex-row lg:flex-wrap lg:items-stretch lg:gap-6">
                            <fieldset className="min-w-0 flex-1 lg:max-w-xl">
                              <legend className="mb-2 block w-full text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
                                WBS outline level
                              </legend>
                              <p className="mb-3 text-[11px] leading-snug text-[#7C8A82]">
                                Limit visible rows to an outline depth range (inclusive). Leave either side empty for no
                                bound on that end.
                              </p>
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                <div className="min-w-0 flex-1 sm:max-w-[11rem]">
                                  <label
                                    htmlFor={wbsMinSelectId}
                                    className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#2A1F17]"
                                  >
                                    Minimum
                                  </label>
                                  <div className="relative">
                                    <select
                                      id={wbsMinSelectId}
                                      value={levelMin === '' ? '' : String(levelMin)}
                                      onChange={(e) =>
                                        setLevelMin(e.target.value === '' ? '' : Number(e.target.value))
                                      }
                                      className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                    >
                                      <option value="">Any</option>
                                      {levelOptions.map((L) => (
                                        <option key={`min-${L}`} value={L}>
                                          Level {L}
                                        </option>
                                      ))}
                                    </select>
                                    <ChevronDown
                                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]"
                                      aria-hidden
                                    />
                                  </div>
                                </div>
                                <div
                                  className="hidden h-11 shrink-0 items-center justify-center pb-0.5 text-[rgba(11,59,46,0.25)] sm:flex"
                                  aria-hidden
                                >
                                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                                </div>
                                <div className="min-w-0 flex-1 sm:max-w-[11rem]">
                                  <label
                                    htmlFor={wbsMaxSelectId}
                                    className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#2A1F17]"
                                  >
                                    Maximum
                                  </label>
                                  <div className="relative">
                                    <select
                                      id={wbsMaxSelectId}
                                      value={levelMax === '' ? '' : String(levelMax)}
                                      onChange={(e) =>
                                        setLevelMax(e.target.value === '' ? '' : Number(e.target.value))
                                      }
                                      className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                    >
                                      <option value="">Any</option>
                                      {levelOptions.map((L) => (
                                        <option key={`max-${L}`} value={L}>
                                          Level {L}
                                        </option>
                                      ))}
                                    </select>
                                    <ChevronDown
                                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]"
                                      aria-hidden
                                    />
                                  </div>
                                </div>
                              </div>
                            </fieldset>

                            <div className="min-w-0 flex-1 lg:min-w-[14rem] lg:max-w-md">
                              <label
                                htmlFor={departmentSelectId}
                                className="mb-1 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]"
                              >
                                Department
                              </label>
                              <p className="mb-2 text-[11px] leading-snug text-[#7C8A82]">
                                Filter by EPC org unit (same list as Team and hierarchy).
                              </p>
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2">
                                <div className="relative min-w-0 flex-1">
                                  <select
                                    id={departmentSelectId}
                                    value={deptFilterId}
                                    onChange={(e) => setDeptFilterId(e.target.value)}
                                    title="Filter by org unit"
                                    className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                  >
                                    <option value="">All departments</option>
                                    {orgUnits.map((u) => (
                                      <option key={orgUnitKey(u)} value={orgUnitKey(u)}>
                                        {u.name}
                                        {u.inactive ? ' (inactive)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown
                                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]"
                                    aria-hidden
                                  />
                                </div>
                                <button
                                  ref={startToEndBtnRef}
                                  id={dateRangeTriggerDomId}
                                  type="button"
                                  aria-haspopup="dialog"
                                  aria-expanded={dateRangePickerOpen}
                                  aria-labelledby={`${dateFilterFieldId}-heading`}
                                  onClick={() => setDateRangePickerOpen((o) => !o)}
                                  className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-[#1F5A47] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)] sm:min-w-[7.5rem]"
                                  title="Choose schedule date range"
                                >
                                  <CalendarRange className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                                  <span className="whitespace-nowrap">
                                    {dateFrom || dateTo
                                      ? [dateFrom && fmtDateShort(dateFrom), dateTo && fmtDateShort(dateTo)].filter(Boolean).join(' → ')
                                      : 'Start to end'}
                                  </span>
                                </button>
                                {(dateFrom || dateTo) && (
                                  <button
                                    type="button"
                                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 text-xs font-semibold text-[#2A1F17] shadow-sm transition hover:bg-[rgba(11,59,46,0.04)]"
                                    title="Clear date filter"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <span id={`${dateFilterFieldId}-heading`} className="sr-only">Start to end</span>
                      <EpcScheduleDateRangePicker
                        hideTrigger
                        anchorRef={startToEndBtnRef}
                        open={dateRangePickerOpen}
                        onOpenChange={setDateRangePickerOpen}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDatesChange={(f, t) => {
                          setDateFrom(f);
                          setDateTo(t);
                        }}
                        accentHex={BRAND.accent}
                        labelId={`${dateFilterFieldId}-heading`}
                      />
                    </div>
                  </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
          </div>

      {/* Full-view backdrop */}
      {gridFullView ? (
        <div
          className="fixed inset-0 z-40 bg-[#2A1F17]/45 backdrop-blur-sm"
          onClick={() => setGridFullView(false)}
          aria-hidden
        />
      ) : null}

      {/* MS Project–style planning grid */}
      <motion.div
        layout
        className={twMerge(
          'overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.96)] shadow-[0_18px_44px_-20px_rgba(42,31,23,0.40)]',
          gridFullView && 'fixed inset-4 z-50 flex flex-col rounded-2xl ring-2 ring-[rgba(201,169,97,0.45)]'
        )}
      >
        {/* Champagne keyline — engraved top rule */}
        <div aria-hidden className="h-[2px] w-full shrink-0 bg-[#C9A961]/70" />
        {/* === Refreshed planning grid toolbar === */}
        <div className="shrink-0 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/80">
          {/* Top row: Title + count · inline search · columns · help */}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#0B3B2E] text-[#F8F2E2] shadow-sm ring-1 ring-[rgba(201,169,97,0.5)]">
                <ListTree className="h-3.5 w-3.5" strokeWidth={2.25} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[13px] font-extrabold leading-tight tracking-tight text-[#0B3B2E]">Planning grid</h3>
                  {currentRevision ? (
                    <span
                      className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-md border border-[rgba(201,169,97,0.5)] bg-[rgba(201,169,97,0.14)] px-1.5 py-0.5 text-[10.5px] font-medium text-[#8A5A08]"
                      title={[
                        `Current MPP revision R${currentRevision.version}`,
                        currentRevision.originalFileName ? `File: ${currentRevision.originalFileName}` : null,
                        currentRevision.source ? `Source: ${currentRevision.source}` : null,
                        currentRevision.createdAt
                          ? `Imported: ${new Date(currentRevision.createdAt).toLocaleString()}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join('\n')}
                    >
                      <History className="h-3 w-3 shrink-0 text-[#8A5A08]" aria-hidden />
                      <span className="rounded bg-[#0B3B2E] px-1 py-px font-mono text-[9px] font-bold tabular-nums text-[#F8F2E2]">
                        R{currentRevision.version}
                      </span>
                      {currentRevision.originalFileName ? (
                        <span className="truncate">{currentRevision.originalFileName}</span>
                      ) : null}
                      {currentRevision.createdAt ? (
                        <span className="shrink-0 text-[9.5px] tabular-nums text-[#8A5A08]/80">
                          {format(new Date(currentRevision.createdAt), 'dd MMM yyyy')}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                  {planningKpis.leafTotal > 0 ? (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-md border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2 py-0.5 text-[10.5px] font-medium text-[#2A1F17]"
                      title={`Average % complete across ${planningKpis.leafTotal} leaf tasks`}
                    >
                      <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#7C8A82]">Avg</span>
                      <span className="relative h-1.5 w-14 overflow-hidden rounded-full bg-[#E7DCC4] ring-1 ring-[rgba(11,59,46,0.10)]">
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-[#339274]"
                          style={{ width: `${Math.max(2, planningKpis.avg)}%` }}
                        />
                      </span>
                      <span className="font-mono text-[10.5px] font-bold tabular-nums text-[#0B3B2E]">
                        {planningKpis.avg}%
                      </span>
                    </span>
                  ) : null}
                </div>
                <p className="text-[10px] leading-tight text-[#7C8A82]">
                  <span className="font-mono font-semibold tabular-nums text-[#2A1F17]">
                    {filteredIndices.length}
                  </span>
                  {filteredIndices.length !== tasks.length ? (
                    <>
                      <span className="mx-1 text-[rgba(11,59,46,0.25)]">/</span>
                      <span className="font-mono tabular-nums text-[#7C8A82]">{tasks.length}</span>
                    </>
                  ) : null}{' '}
                  task{filteredIndices.length === 1 ? '' : 's'}
                  {truncated ? <span className="ml-1 text-[#8A5A08]">· first {MAX_ROWS} shown</span> : null}
                </p>
              </div>
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              {/* Inline quick search */}
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#7C8A82]"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tasks…"
                  aria-label="Search tasks"
                  className="h-7 w-44 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-1 pl-8 pr-7 text-[11.5px] font-medium text-[#2A1F17] placeholder:text-[#7C8A82] transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)] sm:w-56"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-1 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[#7C8A82] hover:bg-[rgba(11,59,46,0.06)] hover:text-[#0B3B2E]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </div>

              {canUpdate ? (
                <button
                  type="button"
                  onClick={() => (tableEdit ? exitTableEdit() : enterTableEdit())}
                  className={twMerge(
                    'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold shadow-sm transition',
                    tableEdit
                      ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white'
                      : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                  )}
                  title={
                    tableEdit
                      ? 'Exit inline edit (Discard keeps nothing)'
                      : 'Edit cells in place — name, dates, duration, %, department, assignee'
                  }
                >
                  <PencilLine className={twMerge('h-3.5 w-3.5', tableEdit ? 'text-[#C9A961]' : 'text-[#339274]')} aria-hidden />
                  {tableEdit ? 'Editing' : 'Edit'}
                </button>
              ) : null}

              {/* Columns chooser */}
              <div className="relative">
                <button
                  ref={columnsMenuAnchorRef}
                  type="button"
                  onClick={() => setColumnsMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={columnsMenuOpen}
                  className={twMerge(
                    'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold shadow-sm transition',
                    columnsMenuOpen
                      ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white'
                      : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                  )}
                  title="Show / hide columns"
                >
                  <Columns3 className={twMerge('h-3.5 w-3.5', columnsMenuOpen ? 'text-[#C9A961]' : 'text-[#339274]')} aria-hidden />
                  <span className="hidden sm:inline">Columns</span>
                  <span
                    className={twMerge(
                      'rounded-md px-1 py-0.5 text-[10px] font-bold tabular-nums',
                      columnsMenuOpen ? 'bg-white/15 text-white' : 'bg-[rgba(11,59,46,0.08)] text-[#0B3B2E]'
                    )}
                  >
                    {visibleOptionalCount}
                  </span>
                  <ChevronDown
                    className={twMerge(
                      'h-3.5 w-3.5 transition-transform',
                      columnsMenuOpen ? 'rotate-180 text-white/70' : 'text-[#7C8A82]'
                    )}
                    aria-hidden
                  />
                </button>
                <AnimatePresence>
                  {columnsMenuOpen ? (
                    <motion.div
                      id="epc-schedule-columns-menu"
                      role="menu"
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute right-0 top-[calc(100%+6px)] z-40 w-80 overflow-hidden rounded-xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.98)] shadow-[0_18px_44px_-20px_rgba(42,31,23,0.45)]"
                    >
                      <div className="flex items-center justify-between gap-2 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2] px-3 py-2">
                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#2A1F17]">
                          Columns
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={resetColWidths}
                            className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-[#2A1F17] transition hover:bg-[rgba(11,59,46,0.06)]"
                            title="Reset all column widths to default"
                          >
                            Reset widths
                          </button>
                          <button
                            type="button"
                            onClick={resetColumns}
                            className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-[#1F5A47] transition hover:bg-[rgba(51,146,116,0.10)]"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="max-h-80 overflow-auto p-1.5">
                        {MS_COLUMNS.map((col) => {
                          const checked = columnVisibility[col.key];
                          return (
                            <label
                              key={col.key}
                              className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-[rgba(11,59,46,0.05)]"
                            >
                              <span
                                className={twMerge(
                                  'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                                  checked
                                    ? 'border-[#264f3c] bg-[#264f3c] text-white'
                                    : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3]'
                                )}
                                aria-hidden
                              >
                                {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                              </span>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleColumn(col.key)}
                                className="sr-only"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5">
                                  <span className="text-[12px] font-semibold text-[#0B3B2E]">{col.label}</span>
                                  <span
                                    className={twMerge(
                                      'rounded-md px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                                      COLUMN_GROUP_ACCENT[col.group]
                                    )}
                                  >
                                    {COLUMN_GROUP_LABEL[col.group]}
                                  </span>
                                </span>
                                <span className="mt-0.5 block text-[10px] leading-snug text-[#7C8A82]">
                                  {col.description}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>

              {/* Full-view expand */}
              <button
                type="button"
                onClick={() => setGridFullView((v) => !v)}
                className={twMerge(
                  'inline-flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm transition',
                  gridFullView
                    ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white'
                    : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                )}
                title={gridFullView ? 'Exit full view' : 'Expand to full view'}
                aria-label={gridFullView ? 'Exit full view' : 'Expand to full view'}
              >
                {gridFullView ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>

              {/* Help / legend popover */}
              <div className="relative">
                <button
                  ref={helpMenuAnchorRef}
                  type="button"
                  onClick={() => setHelpMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={helpMenuOpen}
                  className={twMerge(
                    'inline-flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm transition',
                    helpMenuOpen
                      ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white'
                      : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                  )}
                  title="Legend & shortcuts"
                  aria-label="Legend and shortcuts"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
                <AnimatePresence>
                  {helpMenuOpen ? (
                    <motion.div
                      id="epc-schedule-help-menu"
                      role="menu"
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute right-0 top-[calc(100%+6px)] z-40 w-80 overflow-hidden rounded-xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.98)] shadow-[0_18px_44px_-20px_rgba(42,31,23,0.45)]"
                    >
                      <div className="border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#2A1F17]">
                        Legend
                      </div>
                      <ul className="space-y-1.5 px-3 py-2.5 text-[11px] text-[#2A1F17]">
                        <li className="flex items-center gap-2">
                          <Diamond className="h-3 w-3 shrink-0 fill-[#C9A961] text-[#8A5A08]" />
                          <span>
                            <span className="font-semibold">Milestone</span> — zero-duration checkpoint
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Flag className="h-3 w-3 shrink-0 text-[#1F5A47]" />
                          <span>
                            <span className="font-semibold">Summary</span> — rolls up child tasks (bold row)
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          <StickyNote className="h-3 w-3 shrink-0 text-[#1F5A47]" />
                          <span>
                            <span className="font-semibold">Notes</span> — task has internal notes
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          <MessageCircle className="h-3 w-3 shrink-0 text-[#339274]" />
                          <span>
                            <span className="font-semibold">Discussion</span> — task chat thread
                          </span>
                        </li>
                      </ul>
                      <div className="border-t border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/70 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#2A1F17]">
                        Tips
                      </div>
                      <ul className="space-y-1 px-3 py-2 text-[11px] leading-snug text-[#7C8A82]">
                        <li>· Click a row to open task details</li>
                        <li>· Indented rows are WBS children of the row above</li>
                        <li>· Click a predecessor / successor name to jump to it</li>
                        <li>· Use the chevron on summary rows to collapse / expand</li>
                      </ul>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          </div>

        </div>

        <div
          ref={scheduleScrollRef}
          className={twMerge(
            'w-full min-w-0 max-w-full overflow-auto [scrollbar-gutter:stable]',
            gridFullView ? 'flex-1' : 'max-h-[min(640px,78vh)]'
          )}
        >
          <table
            className="border-separate border-spacing-0 text-left text-[11.5px] text-[#2A1F17]"
            style={{ tableLayout: 'fixed', width: `${totalGridWidth}px`, minWidth: '100%' }}
          >
            <colgroup>
              {columnVisibility.id ? <col style={{ width: `${colWidths.id}px` }} /> : null}
              {columnVisibility.indicators ? <col style={{ width: `${colWidths.indicators}px` }} /> : null}
              <col style={{ width: `${nameColWidth}px` }} />
              {columnVisibility.predecessors ? <col style={{ width: `${colWidths.predecessors}px` }} /> : null}
              {columnVisibility.start ? <col style={{ width: `${colWidths.start}px` }} /> : null}
              {columnVisibility.finish ? <col style={{ width: `${colWidths.finish}px` }} /> : null}
              {columnVisibility.duration ? <col style={{ width: `${colWidths.duration}px` }} /> : null}
              {columnVisibility.percent ? <col style={{ width: `${colWidths.percent}px` }} /> : null}
              {columnVisibility.department ? <col style={{ width: `${colWidths.department}px` }} /> : null}
              {columnVisibility.owner ? <col style={{ width: `${colWidths.owner}px` }} /> : null}
              {columnVisibility.discussion ? <col style={{ width: `${colWidths.discussion}px` }} /> : null}
            </colgroup>
            <thead>
              {/* Column group row (MS Project-style) */}
              <tr className="sticky top-0 z-20">
                {(() => {
                  const groups: { group: MsColumnConfig['group']; count: number }[] = [];
                  const pushGroup = (g: MsColumnConfig['group']) => {
                    const last = groups[groups.length - 1];
                    if (last && last.group === g) last.count += 1;
                    else groups.push({ group: g, count: 1 });
                  };
                  if (columnVisibility.id) pushGroup('task');
                  if (columnVisibility.indicators) pushGroup('task');
                  pushGroup('task'); // task name always
                  if (columnVisibility.predecessors) pushGroup('links');
                  if (columnVisibility.start) pushGroup('schedule');
                  if (columnVisibility.finish) pushGroup('schedule');
                  if (columnVisibility.duration) pushGroup('schedule');
                  if (columnVisibility.percent) pushGroup('progress');
                  if (columnVisibility.department) pushGroup('assignment');
                  if (columnVisibility.owner) pushGroup('assignment');
                  if (columnVisibility.discussion) pushGroup('info');
                  return groups.map((g, idx) => (
                    <th
                      key={`${g.group}-${idx}`}
                      colSpan={g.count}
                      className={twMerge(
                        'border-b border-r border-r-[rgba(11,59,46,0.16)] border-b-[#C9A961] px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] last:border-r-0',
                        COLUMN_GROUP_ACCENT[g.group]
                      )}
                    >
                      {COLUMN_GROUP_LABEL[g.group]}
                    </th>
                  ));
                })()}
              </tr>
              {/* Column label row */}
              <tr className="sticky top-[26px] z-10 bg-[#F8F2E2] shadow-[0_1px_0_0_rgba(201,169,97,0.55)]">
                {columnVisibility.id ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#0B3B2E] select-none">
                    #
                    <div onMouseDown={(e) => startColResize('id', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.indicators ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-1 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#0B3B2E] select-none">
                    <span className="inline-flex items-center gap-0.5" title="Indicators — milestone, summary, notes, chat">
                      <Diamond className="h-3 w-3" />
                      <StickyNote className="h-3 w-3" />
                    </span>
                    <div onMouseDown={(e) => startColResize('indicators', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#0B3B2E] select-none">
                  Task Name
                  <div onMouseDown={startNameColResize} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                </th>
                {columnVisibility.predecessors ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#8A5A08] select-none">
                    <span className="inline-flex items-center gap-1">
                      <ArrowUpFromLine className="h-3 w-3 text-[#C9A961]" />
                      Dependence
                    </span>
                    <div onMouseDown={(e) => startColResize('predecessors', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.start ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-[#1F5A47]" />
                      Start
                    </span>
                    <div onMouseDown={(e) => startColResize('start', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.finish ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-[#7C8A82]" />
                      Finish
                    </span>
                    <div onMouseDown={(e) => startColResize('finish', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.duration ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    Duration
                    <div onMouseDown={(e) => startColResize('duration', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.percent ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    % Complete
                    <div onMouseDown={(e) => startColResize('percent', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.department ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-[#C9A961]" />
                      Department
                    </span>
                    <div onMouseDown={(e) => startColResize('department', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.owner ? (
                  <th className="relative border-b border-r border-[rgba(11,59,46,0.16)] px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    DH
                    <div onMouseDown={(e) => startColResize('owner', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
                {columnVisibility.discussion ? (
                  <th className="relative border-b border-[rgba(11,59,46,0.16)] px-1 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] select-none">
                    <MessageCircle className="mx-auto h-3.5 w-3.5 text-[#339274]" />
                    <div onMouseDown={(e) => startColResize('discussion', e)} className="absolute right-0 top-0 h-full w-2.5 cursor-col-resize border-r-2 border-[rgba(11,59,46,0.16)] hover:border-[#C9A961] hover:bg-[rgba(201,169,97,0.18)] active:border-[#339274] active:bg-[rgba(51,146,116,0.22)]" />
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {displayIndices.map((rowIndex, displayPos) => {
                const t = tableEdit ? (draftTasks[rowIndex] ?? tasks[rowIndex]) : tasks[rowIndex];
                const L = normalizeWbsLevel(t.outlineLevel);
                const sum = isSummaryTask(t);
                const milestone = isTaskMilestone(t);
                const end = subtreeEnd(tasks, rowIndex);
                const hasKids = sum && end > rowIndex + 1;
                const isCollapsed = collapsed.has(rowIndex);
                const msgKey = epcScheduleTaskStableKey(t);
                const msgSummary = messageSummaryByKey[msgKey];
                const hasMsgs = msgSummary != null && msgSummary.total > 0;
                const unread = msgSummary?.unreadFromOthers ?? 0;
                const hasUnread = unread > 0;
                const notesText =
                  t.notes != null ? String(t.notes).trim() : '';
                const hasNotes = notesText.length > 0;
                const percent = parsePercentComplete(t.percentComplete);
                const durationTxt = formatDurationCompact(t.duration);
                const startDay = parseTaskDay(t.start);
                const finishDay = parseTaskDay(t.finish);
                const spanDays = calendarSpanInclusive(t.start, t.finish);
                const isFlashSource = depJump?.from === rowIndex;
                const isFlashTarget = depJump?.to === rowIndex;
                const rowSelected = selectedIndex === rowIndex;

                const rowBase = getWbsTableRowClasses(L, {
                  selected: rowSelected,
                  interactive: true,
                  linkFlash: isFlashSource ? 'source' : isFlashTarget ? 'target' : undefined,
                });

                const cellPad = density === 'compact' ? 'px-2 py-1' : 'px-2.5 py-2';
                const cellAlign = 'align-top';
                const cellBorder = 'border-b border-r border-[rgba(11,59,46,0.10)]';

                return (
                  <motion.tr
                    key={rowIndex}
                    ref={(el) => {
                      if (el) rowElementsRef.current.set(rowIndex, el);
                      else rowElementsRef.current.delete(rowIndex);
                    }}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.18, delay: Math.min(displayPos * 0.008, 0.3) }}
                    onClick={() => {
                      if (tableEdit) return; /* §4.1 — row click selects the cell, not the detail panel */
                      setLinkNavStack([]);
                      lastLinkNavTargetRef.current = null;
                      setSelectedIndex(rowIndex);
                    }}
                    className={twMerge(
                      rowBase,
                      'group/row',
                      sum ? 'font-semibold' : '',
                      milestone && !rowSelected ? 'ring-1 ring-inset ring-[rgba(201,169,97,0.5)]' : ''
                    )}
                  >
                    {columnVisibility.id ? (
                      <td
                        className={twMerge(
                          cellBorder,
                          cellAlign,
                          'bg-[rgba(242,234,219,0.45)] text-center font-mono text-[11px] tabular-nums text-[#7C8A82]',
                          density === 'compact' ? 'px-1 py-1' : 'px-1 py-2'
                        )}
                        title={`Row ${rowIndex + 1} · WBS level ${L}`}
                      >
                        {rowIndex + 1}
                      </td>
                    ) : null}
                    {columnVisibility.indicators ? (
                      <td
                        className={twMerge(
                          cellBorder,
                          cellAlign,
                          'text-center',
                          density === 'compact' ? 'px-1 py-1' : 'px-1 py-2'
                        )}
                      >
                        <div className="flex items-center justify-center gap-0.5">
                          {milestone ? (
                            <span
                              title="Milestone"
                              className="inline-flex h-4 w-4 items-center justify-center"
                              aria-label="Milestone"
                            >
                              <Diamond
                                className="h-3 w-3 fill-[#C9A961] text-[#8A5A08]"
                                strokeWidth={1.5}
                              />
                            </span>
                          ) : sum ? (
                            <span
                              title="Summary task"
                              className="inline-flex h-4 w-4 items-center justify-center text-[#C9A961]"
                              aria-label="Summary"
                            >
                              <Flag className="h-3 w-3" strokeWidth={2.25} />
                            </span>
                          ) : null}
                          {hasNotes ? (
                            <span
                              title={`Notes: ${notesText.slice(0, 80)}${notesText.length > 80 ? '…' : ''}`}
                              className="inline-flex h-4 w-4 items-center justify-center text-[#1F5A47]"
                              aria-label="Has notes"
                            >
                              <StickyNote className="h-3 w-3" strokeWidth={2} />
                            </span>
                          ) : null}
                          {hasUnread ? (
                            <span
                              title={`${unread} unread discussion message${unread === 1 ? '' : 's'}`}
                              className="inline-flex h-4 w-4 items-center justify-center text-[#C9A961]"
                              aria-label="Unread discussion"
                            >
                              <MessageCircle className="h-3 w-3" strokeWidth={2.25} />
                            </span>
                          ) : hasMsgs ? (
                            <span
                              title={`${msgSummary?.total} message${msgSummary?.total === 1 ? '' : 's'}`}
                              className="inline-flex h-4 w-4 items-center justify-center text-[#1F5A47]"
                              aria-label="Has discussion"
                            >
                              <MessageCircle className="h-3 w-3" strokeWidth={2} />
                            </span>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                    {/* Task Name — indented, with expand/collapse chevron for summaries */}
                    <td
                      className={twMerge(cellBorder, cellAlign, 'pr-2', density === 'compact' ? 'py-1 pl-0' : 'py-2 pl-0')}
                    >
                      <div className="flex min-w-0 items-stretch gap-0">
                        <WbsTaskTreeGutter level={L} />
                        <div className={twMerge('flex min-w-0 flex-1 items-start gap-1.5', density === 'compact' ? 'pt-0' : 'pt-0.5')}>
                          {hasKids ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCollapse(rowIndex);
                              }}
                              className="mt-0 shrink-0 rounded p-0.5 text-[#7C8A82] transition hover:bg-[rgba(51,146,116,0.12)] hover:text-[#0B3B2E]"
                              aria-expanded={!isCollapsed}
                              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5" />
                              )}
                            </button>
                          ) : (
                            <span
                              className="mt-0 flex w-[18px] shrink-0 items-start justify-center"
                              aria-hidden
                            >
                              {milestone ? (
                                <Diamond
                                  className="mt-1 h-2.5 w-2.5 fill-[#C9A961] text-[#8A5A08]"
                                  strokeWidth={1.25}
                                />
                              ) : !sum && L > 1 ? (
                                <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[rgba(31,90,71,0.55)] ring-1 ring-[rgba(31,90,71,0.15)]" />
                              ) : null}
                            </span>
                          )}
                          {tableEdit ? (
                            <input
                              value={String(t.name ?? '')}
                              onChange={(e) => updateDraft(rowIndex, { name: e.target.value })}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Task name…"
                              aria-label={`Task name row ${rowIndex + 1}`}
                              className="min-w-0 flex-1 rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1.5 py-0.5 text-[12px] text-[#2A1F17] outline-none focus:border-[#339274] focus:ring-2 focus:ring-[rgba(51,146,116,0.15)]"
                            />
                          ) : (
                            <span
                              className={twMerge(
                                'block min-w-0 flex-1 truncate leading-snug',
                                getTaskNameTextClasses(L, sum)
                              )}
                              title={taskName(t) || undefined}
                            >
                              {taskName(t) || '—'}
                            </span>
                          )}
                          {milestone ? (
                            <span className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded bg-[rgba(201,169,97,0.18)] px-1 py-0 text-[9px] font-bold uppercase tracking-wide text-[#8A5A08] ring-1 ring-[rgba(201,169,97,0.45)]">
                              Milestone
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    {columnVisibility.predecessors ? (
                      <td
                        className={twMerge(cellBorder, cellAlign, cellPad)}
                        title="Click driver name to jump to predecessor"
                      >
                        {(() => {
                          const predLinks = parsePredecessorLinks(t.predecessors);
                          if (predLinks.length) {
                            return (
                              <EpcSchedulePredecessorsColumn
                                links={predLinks}
                                tasks={tasks}
                                fromRowIndex={rowIndex}
                                onJumpToLinkedTask={jumpToLinkedTask}
                              />
                            );
                          }
                          const predText = getPredecessorsMsColumnText(t, tasks);
                          const segs = splitMsDependencySegments(predText);
                          if (!segs.length) {
                            return <span className="text-[#7C8A82]">—</span>;
                          }
                          return (
                            <EpcScheduleRawMsDependencyColumn
                              segments={segs}
                              tasks={tasks}
                              variant="predecessor"
                              fromRowIndex={rowIndex}
                              onJumpToLinkedTask={jumpToLinkedTask}
                            />
                          );
                        })()}
                      </td>
                    ) : null}
                    {columnVisibility.start ? (
                      <td
                        className={twMerge(cellBorder, cellAlign, cellPad, 'whitespace-nowrap tabular-nums')}
                      >
                        {tableEdit ? (
                          <input
                            type="date"
                            value={formatDateIso(t.start)}
                            onChange={(e) => updateDraft(rowIndex, { start: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Start row ${rowIndex + 1}`}
                            className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 font-mono text-[11px] text-[#2A1F17] outline-none focus:border-[#339274]"
                          />
                        ) : startDay ? (
                          <span className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#1F5A47] ring-1 ring-[rgba(31,90,71,0.25)]" aria-hidden />
                            <time dateTime={formatDateIso(t.start) || undefined} className="text-[11px] text-[#2A1F17]">
                              {formatDateCell(t.start)}
                            </time>
                          </span>
                        ) : (
                          <span className="text-[#7C8A82]">—</span>
                        )}
                      </td>
                    ) : null}
                    {columnVisibility.finish ? (
                      <td
                        className={twMerge(cellBorder, cellAlign, cellPad, 'whitespace-nowrap tabular-nums')}
                      >
                        {tableEdit ? (
                          <input
                            type="date"
                            value={formatDateIso(t.finish)}
                            onChange={(e) => updateDraft(rowIndex, { finish: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Finish row ${rowIndex + 1}`}
                            className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 font-mono text-[11px] text-[#2A1F17] outline-none focus:border-[#339274]"
                          />
                        ) : finishDay ? (
                          <span className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A961] ring-1 ring-[rgba(201,169,97,0.35)]" aria-hidden />
                            <time dateTime={formatDateIso(t.finish) || undefined} className="text-[11px] text-[#2A1F17]">
                              {formatDateCell(t.finish)}
                            </time>
                          </span>
                        ) : (
                          <span className="text-[#7C8A82]">—</span>
                        )}
                      </td>
                    ) : null}
                    {columnVisibility.duration ? (
                      <td
                        className={twMerge(cellBorder, cellAlign, cellPad, 'text-right font-mono text-[11px] tabular-nums')}
                        title={t.duration != null ? String(t.duration) : undefined}
                      >
                        {tableEdit ? (
                          (() => {
                            const startDayEdit = parseTaskDay(t.start);
                            const span = calendarSpanInclusive(t.start, t.finish);
                            return (
                              <input
                                type="number"
                                min={1}
                                value={span ?? ''}
                                disabled={!startDayEdit}
                                title={startDayEdit ? 'Calendar days — adjusts Finish' : 'Set Start date first'}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  if (!startDayEdit) return;
                                  const days = Math.max(1, Number(e.target.value) || 1);
                                  updateDraft(rowIndex, {
                                    finish: format(addDays(startDayEdit, days - 1), 'yyyy-MM-dd'),
                                    duration: `${days}d`,
                                  });
                                }}
                                aria-label={`Duration row ${rowIndex + 1}`}
                                className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-right font-mono text-[11px] outline-none focus:border-[#339274] disabled:opacity-40"
                              />
                            );
                          })()
                        ) : durationTxt ? (
                          <span
                            className={twMerge(
                              'inline-flex items-baseline gap-0.5',
                              sum ? 'text-[#0B3B2E]' : 'text-[#2A1F17]'
                            )}
                          >
                            {durationTxt}
                          </span>
                        ) : spanDays != null ? (
                          <span className="text-[#7C8A82]" title={`Calendar span ${spanDays}d`}>
                            {spanDays}d
                          </span>
                        ) : (
                          <span className="text-[#7C8A82]">—</span>
                        )}
                      </td>
                    ) : null}
                    {columnVisibility.percent ? (
                      <td
                        className={twMerge(cellBorder, cellAlign, cellPad)}
                        title={percent != null ? `${percent}% complete` : 'No progress recorded'}
                      >
                        {tableEdit ? (
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={percent ?? 0}
                            onChange={(e) =>
                              updateDraft(rowIndex, { percentComplete: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
                            }
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Percent complete row ${rowIndex + 1}`}
                            className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-right font-mono text-[11px] outline-none focus:border-[#339274]"
                          />
                        ) : percent != null ? (
                          <div className="flex items-center gap-1.5">
                            <div className="relative h-1.5 min-w-[36px] flex-1 overflow-hidden rounded-full bg-[rgba(11,59,46,0.10)] ring-1 ring-[rgba(11,59,46,0.16)]">
                              <div
                                className={twMerge(
                                  'absolute inset-y-0 left-0 rounded-full',
                                  percent >= 100
                                    ? 'bg-[#339274]'
                                    : sum
                                      ? 'bg-[#1F5A47]'
                                      : 'bg-[#C9A961]'
                                )}
                                style={{ width: `${Math.max(2, percent)}%` }}
                              />
                            </div>
                            <span className="w-8 shrink-0 text-right font-mono text-[10px] font-semibold tabular-nums text-[#2A1F17]">
                              {percent}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-[#7C8A82]">—</span>
                        )}
                      </td>
                    ) : null}
                    {columnVisibility.department ? (
                      <td className={twMerge(cellBorder, cellAlign, cellPad)}>
                        {tableEdit ? (
                          <select
                            value={taskOrgUnitId(t) ?? ''}
                            onChange={(e) => patchDraftDepartment(rowIndex, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Department row ${rowIndex + 1}`}
                            className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-[11px] outline-none focus:border-[#339274]"
                          >
                            <option value="">— None —</option>
                            {orgUnits.filter((u2) => !u2.inactive).map((u2) => (
                              <option key={orgUnitKey(u2)} value={orgUnitKey(u2)}>{u2.name}</option>
                            ))}
                          </select>
                        ) : (
                          (() => {
                            const oid = taskOrgUnitId(t);
                            const label = orgUnitName(orgUnits, oid);
                            return label ? (
                              <span className={twMerge(getDepartmentChipClasses(oid), 'max-w-full truncate')} title={label}>
                                <span className={departmentDotClasses(oid ?? '')} aria-hidden />
                                <span className="truncate">{label}</span>
                              </span>
                            ) : (
                              <span className="text-[#7C8A82]">—</span>
                            );
                          })()
                        )}
                      </td>
                    ) : null}
                    {columnVisibility.owner ? (
                      <td className={twMerge(cellBorder, cellAlign, cellPad)}>
                        {tableEdit ? (
                          (() => {
                            const oid = taskOrgUnitId(t);
                            const pool = assignableMembersForOrg(memberships, oid);
                            return (
                              <select
                                value={taskAssignedUserId(t) ?? ''}
                                disabled={!oid}
                                title={oid ? 'Assignee (manager / site engineer in this department)' : 'Set department first'}
                                onChange={(e) => updateDraft(rowIndex, { assignedUserId: e.target.value || undefined })}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Assignee row ${rowIndex + 1}`}
                                className="w-full rounded-md border border-[rgba(11,59,46,0.16)] bg-white px-1 py-0.5 text-[11px] outline-none focus:border-[#339274] disabled:opacity-40"
                              >
                                <option value="">— Unassigned —</option>
                                {pool.map((m) => (
                                  <option key={m.userId} value={m.userId}>{m.userName || m.userEmail || m.userId}</option>
                                ))}
                              </select>
                            );
                          })()
                        ) : (() => {
                          const oid = taskOrgUnitId(t);
                          const head = departmentHeadForOrg(memberships, oid);
                          if (!oid) {
                            return <span className="text-[#7C8A82]">—</span>;
                          }
                          const ownerInitials = (s: string) =>
                            s.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '–';
                          return (
                            <div className="text-[11px] leading-snug text-[#2A1F17]">
                              {head ? (
                                <span className="flex items-center gap-1.5 truncate" title={`Department head: ${head.userName || head.userId}`}>
                                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1F5A47] text-[8.5px] font-bold text-[#F8F2E2] ring-1 ring-[rgba(201,169,97,0.45)]">
                                    {ownerInitials(head.userName || head.userId)}
                                  </span>
                                  <span className="truncate">{head.userName || head.userId}</span>
                                </span>
                              ) : oid ? (
                                <p className="text-[10px] text-[#8A5A08]">No head</p>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                    ) : null}
                    {columnVisibility.discussion ? (
                      <td
                        className={twMerge(
                          'border-b border-[rgba(11,59,46,0.10)] text-center align-middle',
                          density === 'compact' ? 'px-1 py-0.5' : 'px-1 py-1.5'
                        )}
                      >
                        {(() => {
                          const title = hasUnread
                            ? `${unread} unread from others`
                            : hasMsgs && msgSummary
                              ? `${msgSummary.total} message${msgSummary.total === 1 ? '' : 's'}`
                              : 'Open task discussion';
                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTaskMessagesModal({
                                  taskKey: msgKey,
                                  taskTitle: taskName(t) || 'Task',
                                  rowLabel: `Row ${rowIndex + 1} · WBS level ${L || '—'}`,
                                });
                              }}
                              className={twMerge(
                                'relative inline-flex items-center justify-center rounded-lg border text-[#1F5A47] shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[rgba(51,146,116,0.30)]',
                                density === 'compact' ? 'h-6 w-6' : 'h-8 w-8',
                                hasUnread
                                  ? 'border-[#C9A961] bg-[rgba(201,169,97,0.14)] hover:bg-[rgba(201,169,97,0.24)]'
                                  : hasMsgs
                                    ? 'border-[rgba(51,146,116,0.35)] bg-[rgba(51,146,116,0.10)] hover:bg-[rgba(51,146,116,0.18)]'
                                    : 'border-[rgba(11,59,46,0.16)] bg-white hover:bg-[#F8F2E2]'
                              )}
                              title={title}
                              aria-label={title}
                            >
                              <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.25} />
                              {hasUnread ? (
                                <span
                                  className="absolute right-0 top-0 h-2 w-2 animate-pulse rounded-full bg-[#C9A961] ring-2 ring-white"
                                  aria-hidden
                                />
                              ) : null}
                            </button>
                          );
                        })()}
                      </td>
                    ) : null}
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Grid footer — row count, truncation, helper hint */}
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 border-t border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/70 px-3 py-1.5 text-[10.5px] text-[#7C8A82]">
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <span className="text-[#7C8A82]">Showing</span>
            <span className="font-mono font-semibold text-[#0B3B2E]">{displayIndices.length}</span>
            <span className="text-[rgba(11,59,46,0.35)]">of</span>
            <span className="font-mono text-[#2A1F17]">{filteredIndices.length}</span>
            {filteredIndices.length !== tasks.length ? (
              <span className="text-[rgba(11,59,46,0.35)]">
                · {tasks.length} total
              </span>
            ) : null}
          </span>
          {truncated ? (
            <span className="inline-flex items-center gap-1 font-medium text-[#8A5A08]">
              <AlertTriangle className="h-3 w-3" />
              First {MAX_ROWS} rows shown — tighten filters for the rest
            </span>
          ) : (
            <span className="hidden text-[10px] text-[#7C8A82]/80 sm:inline">
              Click a row for details · click a predecessor to jump
            </span>
          )}
        </div>

        {filteredIndices.length === 0 ? (
          <div className="border-t border-[rgba(11,59,46,0.10)] px-4 py-10 text-center text-sm text-[#7C8A82]">
            No tasks match the current filters.{' '}
            <button
              type="button"
              onClick={clearFilters}
              className="font-semibold text-[#1F5A47] underline-offset-2 hover:underline"
            >
              Reset filters
            </button>
          </div>
        ) : null}
      </motion.div>

      {tableEdit ? (
        <div className="sticky bottom-3 z-30 mx-auto flex w-fit flex-wrap items-center gap-3 rounded-xl border border-[rgba(201,169,97,0.4)] bg-[#0B3B2E] px-4 py-2.5 text-[12px] text-[#F8F2E2] shadow-[0_18px_44px_-16px_rgba(42,31,23,0.6)]">
          <span className="font-bold tabular-nums text-white">
            {tableEditChangeCount} change{tableEditChangeCount === 1 ? '' : 's'}
          </span>
          <span className="text-[10.5px] text-[#F8F2E2]/60">
            {currentVersion && currentVersion > 0
              ? `Saving creates revision R${currentVersion + 1}`
              : 'Saving creates a new plan revision'}
          </span>
          <button
            type="button"
            onClick={exitTableEdit}
            disabled={saving}
            className="rounded-lg px-2.5 py-1 font-semibold text-[#F8F2E2]/80 transition hover:bg-white/10 disabled:opacity-50"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => void saveTableEdit()}
            disabled={saving || tableEditChangeCount === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#C9A961] px-3 py-1 font-bold text-[#2A1F17] transition hover:bg-[#d4b877] disabled:opacity-40"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </button>
        </div>
      ) : null}
        </>
      ) : null}

      {studioMode === 'timeline' ? (
        <>
          {/* Timeline filter toolbar — same state as the table filters */}
          <div className="mb-3 rounded-2xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.96)] shadow-[0_14px_36px_-22px_rgba(42,31,23,0.40)]">
            <div className="flex flex-wrap items-center gap-2 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/80 px-3 py-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                className={twMerge(
                  'inline-flex min-h-[2.25rem] items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition',
                  filtersOpen
                    ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white shadow-sm'
                    : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
                )}
              >
                <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#C9A961]" aria-hidden />
                <span>Filters</span>
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-[#C9A961] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#2A1F17]">
                    {activeFilterCount}
                  </span>
                ) : null}
                <ChevronDown
                  className={twMerge('h-4 w-4 shrink-0 transition-transform', filtersOpen ? 'rotate-180 text-white/70' : 'text-[#7C8A82]')}
                  aria-hidden
                />
              </button>
              <span
                className="inline-flex items-center rounded-md border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2 py-1 font-mono text-[11px] tabular-nums text-[#7C8A82]"
                title="Rows matching filters · total tasks in schedule"
              >
                <span className="font-semibold text-[#0B3B2E]">{filteredIndices.length}</span>
                <span className="mx-0.5 text-[rgba(11,59,46,0.25)]">/</span>
                {tasks.length}
              </span>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1F5A47] hover:bg-[rgba(51,146,116,0.10)]"
                >
                  <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Clear all
                </button>
              ) : null}
            </div>

            <AnimatePresence initial={false}>
              {filtersOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-visible"
                >
                  <div className="border-t border-[rgba(11,59,46,0.10)] bg-[#F2EADB]/50 px-2 py-2 sm:px-2.5 sm:py-2">
                    <div className="flex flex-col gap-2.5 sm:gap-3" role="group" aria-label="Timeline filters">
                      <div className="overflow-hidden rounded-lg border border-[rgba(11,59,46,0.22)] bg-[#FCFBF6] shadow-sm">
                        <div className="flex flex-col gap-4 p-3 sm:p-4">
                          {/* Search */}
                          <label className="group/find min-w-0">
                            <span className="relative z-20 mb-2 flex items-start gap-3">
                              <span className="relative z-20 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0B3B2E] text-[#F8F2E2] shadow-sm ring-1 ring-[rgba(201,169,97,0.5)]" aria-hidden>
                                <Search className="h-4 w-4" strokeWidth={2.5} />
                              </span>
                              <span className="min-w-0 pt-0.5">
                                <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#2A1F17]">Find in schedule</span>
                                <span className="mt-0.5 block text-[11px] font-medium leading-snug text-[#7C8A82]">Match task name, WBS codes, predecessors, successors, or resource lines.</span>
                              </span>
                            </span>
                            <div className="relative z-10 overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] shadow-sm transition-[box-shadow,border-color] focus-within:z-20 focus-within:border-[#0B3B2E] focus-within:ring-2 focus-within:ring-[rgba(166,206,57,0.22)]">
                              <div className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-[#C9A961]" aria-hidden />
                              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[1.125rem] w-[1.125rem] -translate-y-1/2 text-[#339274] transition group-focus-within/find:scale-105 group-focus-within/find:text-[#1F5A47]" aria-hidden />
                              <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search the live schedule…"
                                autoComplete="off"
                                className="relative w-full min-h-11 rounded-2xl border-0 bg-transparent py-2.5 pl-11 pr-3 text-sm font-medium text-[#2A1F17] placeholder:font-normal placeholder:text-[#7C8A82] focus:outline-none"
                              />
                            </div>
                          </label>

                          <div className="flex flex-col gap-4 border-t border-[rgba(11,59,46,0.10)] pt-4 lg:flex-row lg:flex-wrap lg:items-stretch lg:gap-6">
                            {/* WBS level */}
                            <fieldset className="min-w-0 flex-1 lg:max-w-xl">
                              <legend className="mb-2 block w-full text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">WBS outline level</legend>
                              <p className="mb-3 text-[11px] leading-snug text-[#7C8A82]">Limit visible rows to a depth range (inclusive).</p>
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                <div className="min-w-0 flex-1 sm:max-w-[11rem]">
                                  <label htmlFor={`${timelineScopeFilterFieldId}-wbs-min`} className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#2A1F17]">Minimum</label>
                                  <div className="relative">
                                    <select
                                      id={`${timelineScopeFilterFieldId}-wbs-min`}
                                      value={levelMin === '' ? '' : String(levelMin)}
                                      onChange={(e) => setLevelMin(e.target.value === '' ? '' : Number(e.target.value))}
                                      className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                    >
                                      <option value="">Any</option>
                                      {levelOptions.map((L) => <option key={`tl-min-${L}`} value={L}>Level {L}</option>)}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]" aria-hidden />
                                  </div>
                                </div>
                                <div className="hidden h-11 shrink-0 items-center justify-center pb-0.5 text-[rgba(11,59,46,0.25)] sm:flex" aria-hidden>
                                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                                </div>
                                <div className="min-w-0 flex-1 sm:max-w-[11rem]">
                                  <label htmlFor={`${timelineScopeFilterFieldId}-wbs-max`} className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#2A1F17]">Maximum</label>
                                  <div className="relative">
                                    <select
                                      id={`${timelineScopeFilterFieldId}-wbs-max`}
                                      value={levelMax === '' ? '' : String(levelMax)}
                                      onChange={(e) => setLevelMax(e.target.value === '' ? '' : Number(e.target.value))}
                                      className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                    >
                                      <option value="">Any</option>
                                      {levelOptions.map((L) => <option key={`tl-max-${L}`} value={L}>Level {L}</option>)}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]" aria-hidden />
                                  </div>
                                </div>
                              </div>
                            </fieldset>

                            {/* Department + date */}
                            <div className="min-w-0 flex-1 lg:min-w-[14rem] lg:max-w-md">
                              <label htmlFor={`${timelineScopeFilterFieldId}-department`} className="mb-1 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">Department</label>
                              <p className="mb-2 text-[11px] leading-snug text-[#7C8A82]">Filter by EPC org unit.</p>
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2">
                                <div className="relative min-w-0 flex-1">
                                  <select
                                    id={`${timelineScopeFilterFieldId}-department`}
                                    value={deptFilterId}
                                    onChange={(e) => setDeptFilterId(e.target.value)}
                                    className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-3 pr-10 text-sm font-medium text-[#2A1F17] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] focus:border-[#0B3B2E] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)]"
                                  >
                                    <option value="">All departments</option>
                                    {orgUnits.map((u) => (
                                      <option key={orgUnitKey(u)} value={orgUnitKey(u)}>
                                        {u.name}{u.inactive ? ' (inactive)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7C8A82]" aria-hidden />
                                </div>
                                <button
                                  ref={timelineStartToEndBtnRef}
                                  type="button"
                                  aria-haspopup="dialog"
                                  aria-expanded={dateRangePickerOpen}
                                  aria-labelledby={`${timelineDateFilterFieldId}-heading`}
                                  onClick={() => setDateRangePickerOpen((o) => !o)}
                                  className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-[#1F5A47] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)] focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.22)] sm:min-w-[7.5rem]"
                                  title="Choose schedule date range"
                                >
                                  <CalendarRange className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                                  <span className="whitespace-nowrap">
                                    {dateFrom || dateTo
                                      ? [dateFrom && fmtDateShort(dateFrom), dateTo && fmtDateShort(dateTo)].filter(Boolean).join(' → ')
                                      : 'Start to end'}
                                  </span>
                                </button>
                                {(dateFrom || dateTo) ? (
                                  <button
                                    type="button"
                                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 text-xs font-semibold text-[#2A1F17] shadow-sm transition hover:bg-[rgba(11,59,46,0.04)]"
                                    title="Clear date filter"
                                  >
                                    ✕
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <span id={`${timelineDateFilterFieldId}-heading`} className="sr-only">Start to end</span>
                      <EpcScheduleDateRangePicker
                        hideTrigger
                        anchorRef={timelineStartToEndBtnRef}
                        open={dateRangePickerOpen}
                        onOpenChange={setDateRangePickerOpen}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDatesChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
                        accentHex={BRAND.accent}
                        labelId={`${timelineDateFilterFieldId}-heading`}
                      />
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <EpcScheduleTimeline
            tasks={tasks}
            filteredIndices={filteredIndices}
            selectedIndex={selectedIndex}
            onSelectIndex={(idx) => {
              setLinkNavStack([]);
              lastLinkNavTargetRef.current = null;
              setSelectedIndex(idx);
            }}
          />
        </>
      ) : null}

      {studioMode === 'departments' ? (
        <EpcScheduleDepartmentAssignView
          tasks={deptAssignTasks}
          onChange={canUpdate ? setDeptAssignTasks : () => {}}
          orgUnits={orgUnits}
          memberships={memberships}
          canUpdate={Boolean(canUpdate)}
          saving={saving}
          dirty={dirtyDeptAssign}
          onSave={persistDeptSchedule}
        />
      ) : null}

      {studioMode === 'members' ? (
        mine && currentUserId && (mine.role === 'department_head' || mine.role === 'manager') ? (
          <EpcScheduleMemberAssignView
            tasks={memberAssignTasks}
            onChange={canUpdate ? setMemberAssignTasks : () => {}}
            orgUnits={orgUnits}
            memberships={memberships}
            mine={mine}
            myUserId={currentUserId}
            saving={saving}
            dirty={dirtyMemberAssign}
            onSave={persistMemberSchedule}
            canUpdate={Boolean(canUpdate)}
          />
        ) : (
          <div className="rounded-2xl border border-[rgba(201,169,97,0.5)] bg-[rgba(201,169,97,0.12)] px-4 py-6 text-center text-sm text-[#8A5A08]">
            Member assignment is available to <b>department heads</b> and <b>managers</b>. Switch to
            another view from the toolbar above.
          </div>
        )
      ) : null}

      {studioMode === 'manage' && canUpdate ? (
        <EpcScheduleEditor
          tasks={editedTasks}
          onChange={setEditedTasks}
          onSave={persistSchedule}
          saving={saving}
          dirty={dirtyManage}
          orgUnits={orgUnits}
          memberships={memberships}
          onImportMpp={() => navigate('../mpp', { relative: 'path' })}
          onExportCsv={() =>
            downloadTasksCsv(
              editedTasks,
              `${projectCode.replace(/[^a-zA-Z0-9._-]/g, '_')}-tasks.csv`
            )
          }
          onExportXml={() => void exportMpp()}
          exportingXml={exportingMpp}
          isFullView={studioFullView}
          onToggleFullView={toggleStudioFullView}
        />
      ) : null}
      {studioMode === 'manage' && !canUpdate ? (
        <div className="rounded-2xl border border-[rgba(201,169,97,0.5)] bg-[rgba(201,169,97,0.12)] px-4 py-6 text-center text-sm text-[#8A5A08]">
          Editing the schedule requires{' '}
          <code className="rounded bg-white/80 px-1 font-mono text-xs">epcProject.update</code> permission.
        </div>
      ) : null}

      {/* Task detail panel (read mode) */}
      <EpcScheduleTaskDetailPanel
        open={selectedIndex != null && !tableEdit}
        tasks={tasks}
        index={selectedIndex}
        orgUnits={orgUnits}
        memberships={memberships}
        messageSummaryByKey={messageSummaryByKey}
        onClose={() => setSelectedIndex(null)}
        onOpenDiscussion={(taskKey, taskTitle, rowLabel) =>
          setTaskMessagesModal({ taskKey, taskTitle, rowLabel })
        }
      />

      <EpcScheduleTaskMessagesModal
        open={taskMessagesModal != null}
        onClose={() => setTaskMessagesModal(null)}
        projectId={projectId}
        taskKey={taskMessagesModal?.taskKey ?? ''}
        taskTitle={taskMessagesModal?.taskTitle ?? ''}
        rowLabel={taskMessagesModal?.rowLabel ?? ''}
        memberships={memberships}
        currentUserId={currentUserId}
        onThreadSummarySync={syncTaskMessageSummary}
      />
    </div>
  );
}
